import React, { useState, useEffect } from "react";
import Layout from "@/components/Layout";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { toast } from "sonner";
import { api, getApiErrorMessage } from "@/lib/api";
import { TANK_SENSOR_TYPE_OPTIONS } from "@/lib/tankSensorTypes";
import {
  GSM_APN_OPTIONS,
  LEVEL_CONNECTION_TYPE_OPTIONS,
} from "@/lib/networkConnectionTypes";

import {
  Building2,
  Cpu,
  Droplets,
  Power,
  Network,
  GitBranch,
  X,
  Plus,
  ChevronRight,
  Layers,
  Waves,
  Zap,
  FlaskConical,
  Activity,
  Edit2,
  Trash2,
  Users,
} from "lucide-react";

const MAX_ROWS = 20;
const MAX_TANK_ROWS = 32;
const SOURCE_STATUS_OPTIONS = [
  { value: "0", label: "Not Connected" },
  { value: "1", label: "Connected" },
];
const SOURCE_SENSOR_TYPE_OPTIONS = [
  { value: "1", label: "Hydrostatic (BS8PT)" },
  { value: "2", label: "Hydrostatic (BS8L1)" },
  { value: "3", label: "TL_Float" },
  { value: "4", label: "TL_Online" },
];
const MODBUS_DATA_TYPE_OPTIONS = [
  { value: "1", label: "U16" },
  { value: "2", label: "S16" },
  { value: "3", label: "U32" },
  { value: "4", label: "S32" },
  { value: "5", label: "FLOAT32" },
  { value: "6", label: "SPLIT_INT_DEC" },
];
const MODBUS_ORDER_OPTIONS = [
  { value: "0", label: "NA" },
  { value: "1", label: "ABCD" },
  { value: "2", label: "BADC" },
  { value: "3", label: "CDAB" },
  { value: "4", label: "DCBA" },
];
const CONNECTED_STATUS_OPTIONS = [
  { value: "1", label: "Connected" },
  { value: "0", label: "Not Connected" },
];
const ENERGY_METER_MAKE_OPTIONS = [{ value: "1", label: "SELECT_EM4M3PC100A" }];
const PUMP_CONTROL_TYPE_OPTIONS = [
  { value: "0", label: "Direct" },
  { value: "1", label: "VFD" },
  { value: "2", label: "PressureRange" },
  { value: "3", label: "VFD_Direct" },
];
const OPTIONAL_TOWER_VALUE = "__no_tower__";
const isVfdPumpControlType = (controlType) => [1, 3].includes(Number(controlType));
const normalizeEnergyMeterMake = (value) => {
  const text = String(value ?? "").trim();
  const option = ENERGY_METER_MAKE_OPTIONS.find(
    (item) => item.value === text || item.label === text,
  );
  return option?.value || ENERGY_METER_MAKE_OPTIONS[0].value;
};

const cleanFlowId = (value) => {
  if (value === null || value === undefined) return "";
  const text = String(value).trim();
  return ["", "-", "null", "none", "undefined"].includes(text.toLowerCase())
    ? ""
    : text;
};

const uniqueFlowIds = (values = []) =>
  values.reduce((ids, value) => {
    const cleaned = cleanFlowId(value);
    return cleaned && !ids.includes(cleaned) ? [...ids, cleaned] : ids;
  }, []);

// Pumps connected to a tank mapping as [{ pump_id, pump_controller_id }].
// A tank can be fed by several pumps; older mappings hold a single pump in
// pump_id / pump_controller_id, which is read as a one-item list.
const getMappingPumps = (mapping = {}) => {
  const pumps = [];
  (Array.isArray(mapping.pumps) ? mapping.pumps : []).forEach((item) => {
    const pumpId = cleanFlowId(item?.pump_id);
    if (!pumpId || pumps.some((pump) => pump.pump_id === pumpId)) return;
    const pumpControllerId = cleanFlowId(item?.pump_controller_id);
    pumps.push(
      pumpControllerId
        ? { pump_id: pumpId, pump_controller_id: pumpControllerId }
        : { pump_id: pumpId },
    );
  });
  if (pumps.length === 0 && cleanFlowId(mapping.pump_id)) {
    const pumpControllerId = cleanFlowId(mapping.pump_controller_id);
    pumps.push(
      pumpControllerId
        ? { pump_id: cleanFlowId(mapping.pump_id), pump_controller_id: pumpControllerId }
        : { pump_id: cleanFlowId(mapping.pump_id) },
    );
  }
  return pumps;
};

const normalizeVTMapping = (mapping = {}) => {
  const pumps = getMappingPumps(mapping);
  const normalized = {
    valve_id: cleanFlowId(mapping.valve_id),
    tank_id: cleanFlowId(mapping.tank_id),
    // pump_id / pump_controller_id mirror the first pump for older readers.
    pump_id: pumps[0]?.pump_id || cleanFlowId(mapping.pump_id),
    pump_controller_id: pumps.length
      ? pumps[0].pump_controller_id || ""
      : cleanFlowId(mapping.pump_controller_id),
  };

  const result = Object.fromEntries(
    Object.entries(normalized).filter(([, value]) => Boolean(value)),
  );
  if (pumps.length) result.pumps = pumps;
  return result;
};

const getFlowPumpControllerIds = (flow = {}) =>
  uniqueFlowIds([
    ...(Array.isArray(flow.pump_controller_ids)
      ? flow.pump_controller_ids
      : []),
    flow.pump_controller_id,
    ...getFlowLevelControllerMappings(flow).flatMap((entry) =>
      (entry.valve_tank_mappings || []).flatMap((mapping) => [
        mapping.pump_controller_id,
        ...getMappingPumps(mapping).map((pump) => pump.pump_controller_id),
      ]),
    ),
  ]);

const getFlowLevelControllerMappings = (flow = {}) => {
  const nested = Array.isArray(flow.level_controller_mappings)
    ? flow.level_controller_mappings
        .map((entry) => ({
          level_controller_id: cleanFlowId(entry.level_controller_id),
          valve_tank_mappings: (entry.valve_tank_mappings || [])
            .map(normalizeVTMapping)
            .filter((mapping) => Object.keys(mapping).length > 0),
        }))
        .filter(
          (entry) =>
            entry.level_controller_id || entry.valve_tank_mappings.length > 0,
        )
    : [];

  if (nested.length > 0) return nested;

  const levelControllerId = cleanFlowId(flow.level_controller_id);
  const mappings = (flow.valve_tank_mappings || [])
    .map(normalizeVTMapping)
    .filter((mapping) => Object.keys(mapping).length > 0);

  return levelControllerId || mappings.length > 0
    ? [
        {
          level_controller_id: levelControllerId,
          valve_tank_mappings: mappings,
        },
      ]
    : [];
};

const getFlowLevelControllerIds = (flow = {}) =>
  uniqueFlowIds(
    getFlowLevelControllerMappings(flow).map(
      (entry) => entry.level_controller_id,
    ),
  );

const getFlowValveTankMappings = (flow = {}) =>
  getFlowLevelControllerMappings(flow).flatMap((entry) =>
    (entry.valve_tank_mappings || []).map((mapping) => ({
      ...mapping,
      level_controller_id: entry.level_controller_id,
    })),
  );

const getEmptyATBPCPumpConfiguration = () => ({
  pump_id: "",
  destination_tank_id: "",
  source_id: "",
  flowmeter_id: "",
});

const normalizeATBPCPumpConfiguration = (mapping = {}) => ({
  pump_id: cleanFlowId(mapping.pump_id),
  destination_tank_id: cleanFlowId(mapping.destination_tank_id),
  source_id: cleanFlowId(mapping.source_id),
  flowmeter_id: cleanFlowId(mapping.flowmeter_id),
});

const normalizeATBPCConfig = (config = {}) => {
  const atbpc_device_id = cleanFlowId(config?.atbpc_device_id);
  const pump_configurations = Array.isArray(config?.pump_configurations)
    ? config.pump_configurations.map(normalizeATBPCPumpConfiguration)
    : [];

  return {
    atbpc_device_id,
    pump_configurations,
  };
};

const normalizeFlowForForm = (flow = {}, societyId = "") => {
  const level_controller_mappings = getFlowLevelControllerMappings(flow);
  const level_controller_ids = getFlowLevelControllerIds(flow);
  const pump_controller_ids = getFlowPumpControllerIds(flow);
  const valve_tank_mappings = getFlowValveTankMappings({
    ...flow,
    level_controller_mappings,
  });
  const atbpc_config = normalizeATBPCConfig(flow.atbpc_config);

  return {
    ...flow,
    society_id: flow.society_id || societyId || "",
    pump_controller_ids,
    pump_controller_id: pump_controller_ids[0] || "",
    level_controller_mappings,
    level_controller_id: level_controller_ids[0] || "",
    valve_tank_mappings,
    atbpc_config,
  };
};

/* ─────────────────────────────────────────────
   Beautiful Flow Visualization with Colorful Blocks
───────────────────────────────────────────── */
const findAsset = (list, idFields, id) => {
  if (!id) return null;
  const items = Array.isArray(list) ? list : list ? [list] : [];
  const fields = Array.isArray(idFields) ? idFields : [idFields];
  return (
    items.find((item) =>
      fields.some((field) => String(item?.[field] || "") === String(id)),
    ) || null
  );
};

const getAssetName = (list, idFields, id, fallbackValue = "") => {
  if (!id && !fallbackValue) return "";
  const found = findAsset(list, idFields, id);
  return found?.name || found?.sensor_name || fallbackValue || id || "";
};

const getFirstAssetName = (candidates, id, fallbackValue = "") => {
  if (!id && !fallbackValue) return "";

  for (const candidate of candidates) {
    const found = findAsset(candidate.list, candidate.idFields, id);
    if (found?.name || found?.sensor_name) {
      return found.name || found.sensor_name;
    }
  }

  return fallbackValue || id || "";
};

const FlowTree = ({
  flow,
  societiesData,
  adminsData,
  ugTanksData,
  towersData,
  pumpControllersData,
  borewellPumpControllersData,
  pumpsData,
  levelControllersData,
  tanksData,
  valvesData,
  sourcesData,
  flowmetersData,
}) => {
  const label = (list, idField, id, displayField = "name") => {
    if (!list || !list.length) return id;
    const found = list.find((x) => x[idField] === id);
    return found ? found[displayField] : id;
  };
  const sourceAssetName = (sourceId, fallbackValue = "") =>
    getFirstAssetName(
      [
        { list: sourcesData, idFields: "source_id" },
        { list: tanksData, idFields: "tank_id" },
        { list: ugTanksData, idFields: "ug_tank_id" },
      ],
      sourceId,
      fallbackValue,
    );
  const destinationTankName = (tankId, fallbackValue = "") =>
    getFirstAssetName(
      [
        { list: tanksData, idFields: "tank_id" },
        { list: ugTanksData, idFields: "ug_tank_id" },
      ],
      tankId,
      fallbackValue,
    );

  const assignedUserIds =
    Array.isArray(flow.user_ids) && flow.user_ids.length
      ? flow.user_ids
      : flow.user_id
        ? [flow.user_id]
        : [];
  const assignedUsersLabel = assignedUserIds
    .map((id) => {
      const admin = adminsData.find((item) => item.id === id);
      return admin?.name || admin?.email || id;
    })
    .filter(Boolean)
    .join(", ");
  const pumpControllerIds = getFlowPumpControllerIds(flow);
  const levelControllerMappings = getFlowLevelControllerMappings(flow);
  const atbpcConfig = normalizeATBPCConfig(flow.atbpc_config);

  const FlowBlock = ({ icon: Icon, title, value, bgColor, isSmall }) => (
    <div
      className={`${bgColor} rounded-lg shadow-md hover:shadow-lg transition-all ${isSmall ? "px-2.5 py-1.5" : "px-3 py-2"}`}
    >
      <div
        className={`flex items-center gap-1.5 text-white ${isSmall ? "min-w-[75px]" : "min-w-[110px]"}`}
      >
        <Icon className={`flex-shrink-0 ${isSmall ? "h-3 w-3" : "h-4 w-4"}`} />
        <div className="min-w-0">
          <div
            className={`font-semibold opacity-90 leading-tight ${isSmall ? "text-[9px]" : "text-xs"}`}
          >
            {title}
          </div>
          <div
            className={`font-bold truncate ${isSmall ? "text-[8px]" : "text-xs"}`}
          >
            {value}
          </div>
        </div>
      </div>
    </div>
  );

  const Arrow = ({ isSmall }) => (
    <div
      className={`flex-shrink-0 text-slate-400 ${isSmall ? "px-1" : "px-2"}`}
    >
      <ChevronRight className={isSmall ? "h-4 w-4" : "h-5 w-5"} />
    </div>
  );

  const mappingLabel = (mapping, field, list, idField) => {
    const displayValue = mapping[`${field.replace("_id", "")}_display`];
    return getAssetName(list, idField, mapping[field], displayValue);
  };

  const flowmeterLabel = (flowmeterId, displayValue) => {
    const found = flowmetersData.find((item) => item.id === flowmeterId);
    return (
      found?.name ||
      displayValue ||
      (found ? `Slave ${found.slave_id}` : flowmeterId)
    );
  };

  const MappingRow = ({ mapping }) => {
    const pumps = getMappingPumps(mapping);
    const blocks = [
      // A controller without any pump is still shown on its own.
      !pumps.length &&
        mapping.pump_controller_id && {
          key: "pump_controller",
          icon: Zap,
          title: "Pump Ctrl",
          value: getAssetName(
            pumpControllersData,
            "device_id",
            mapping.pump_controller_id,
          ),
          bgColor: "bg-gradient-to-br from-yellow-500 to-yellow-600",
        },
      mapping.tank_id && {
        key: "tank",
        icon: Waves,
        title: "Tank",
        value: mappingLabel(mapping, "tank_id", tanksData, "tank_id"),
        bgColor: "bg-gradient-to-r from-teal-500 to-teal-600",
      },
      mapping.valve_id && {
        key: "valve",
        icon: FlaskConical,
        title: "Valve",
        value: mappingLabel(mapping, "valve_id", valvesData, "valve_id"),
        bgColor: "bg-gradient-to-r from-pink-500 to-pink-600",
      },
    ].filter(Boolean);

    return (
      <div className="flex items-center gap-0">
        {blocks.map((block, index) => (
          <React.Fragment key={block.key}>
            <FlowBlock {...block} isSmall={true} />
            {(index < blocks.length - 1 || pumps.length > 0) && (
              <Arrow isSmall={true} />
            )}
          </React.Fragment>
        ))}
        {/* Every pump feeding this tank, each with the controller that runs it */}
        {pumps.length > 0 && (
          <div className="flex flex-col gap-1">
            {pumps.map((pump) => (
              <div key={pump.pump_id} className="flex items-center gap-0">
                {pump.pump_controller_id && (
                  <>
                    <FlowBlock
                      icon={Zap}
                      title="Pump Ctrl"
                      value={getAssetName(
                        pumpControllersData,
                        "device_id",
                        pump.pump_controller_id,
                      )}
                      bgColor="bg-gradient-to-br from-yellow-500 to-yellow-600"
                      isSmall={true}
                    />
                    <Arrow isSmall={true} />
                  </>
                )}
                <FlowBlock
                  icon={Activity}
                  title={pumps.length > 1 ? `Pump ${pumps.indexOf(pump) + 1}` : "Pump"}
                  value={getAssetName(pumpsData, "pump_id", pump.pump_id)}
                  bgColor="bg-gradient-to-br from-orange-500 to-orange-600"
                  isSmall={true}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-2">
      {/* Main Flow */}
      <div className="overflow-x-auto pb-2 -mx-2 px-2">
        <div className="flex items-start gap-0 min-w-max">
          <div className="flex items-center gap-0 pt-2">
            {/* Society */}
            <FlowBlock
              icon={Building2}
              title="Society"
              value={label(
                societiesData ? [societiesData] : [],
                "id",
                flow.society_id,
              )}
              bgColor="bg-gradient-to-br from-blue-500 to-blue-600"
            />
            <Arrow />

            {atbpcConfig.atbpc_device_id && (
              <>
                <div className="flex flex-col gap-2">
                  <FlowBlock
                    icon={Power}
                    title="ATBPC"
                    value={getAssetName(
                      borewellPumpControllersData,
                      "device_id",
                      atbpcConfig.atbpc_device_id,
                    )}
                    bgColor="bg-gradient-to-br from-emerald-500 to-emerald-600"
                  />
                  {(atbpcConfig.pump_configurations || []).map(
                    (mapping, index) => (
                      <div
                        key={`${mapping.pump_id}-${index}`}
                        className="flex items-center gap-0"
                      >
                        <FlowBlock
                          icon={Droplets}
                          title="Source"
                          value={sourceAssetName(
                            mapping.source_id,
                            mapping.source_display,
                          )}
                          bgColor="bg-gradient-to-br from-sky-500 to-sky-600"
                          isSmall={true}
                        />
                        <Arrow isSmall={true} />
                        <FlowBlock
                          icon={Activity}
                          title="Pump"
                          value={getAssetName(
                            pumpsData,
                            "pump_id",
                            mapping.pump_id,
                            mapping.pump_display,
                          )}
                          bgColor="bg-gradient-to-br from-orange-500 to-orange-600"
                          isSmall={true}
                        />
                        {mapping.flowmeter_id && (
                          <>
                            <Arrow isSmall={true} />
                            <FlowBlock
                              icon={Waves}
                              title="Flowmeter"
                              value={flowmeterLabel(
                                mapping.flowmeter_id,
                                mapping.flowmeter_display,
                              )}
                              bgColor="bg-gradient-to-br from-cyan-500 to-cyan-600"
                              isSmall={true}
                            />
                          </>
                        )}
                        <Arrow isSmall={true} />
                        <FlowBlock
                          icon={Layers}
                          title="Destination"
                          value={destinationTankName(
                            mapping.destination_tank_id,
                            mapping.destination_tank_display,
                          )}
                          bgColor="bg-gradient-to-br from-teal-500 to-teal-600"
                          isSmall={true}
                        />
                      </div>
                    ),
                  )}
                </div>
                <Arrow />
              </>
            )}

            {/* UG Tank - Only if selected */}
            {flow.ug_tank_id && (
              <>
                <FlowBlock
                  icon={Droplets}
                  title="UG Tank"
                  value={getAssetName(
                    ugTanksData,
                    "ug_tank_id",
                    flow.ug_tank_id,
                  )}
                  bgColor="bg-gradient-to-br from-cyan-500 to-cyan-600"
                />
                <Arrow />
              </>
            )}

            {pumpControllerIds.length > 0 && (
              <>
                <div className="flex flex-col gap-2">
                  {pumpControllerIds.map((controllerId) => (
                    <FlowBlock
                      key={controllerId}
                      icon={Zap}
                      title="Pump Ctrl"
                      value={getAssetName(
                        pumpControllersData,
                        "device_id",
                        controllerId,
                      )}
                      bgColor="bg-gradient-to-br from-yellow-500 to-yellow-600"
                    />
                  ))}
                </div>
                {(flow.tower_id ||
                  assignedUsersLabel ||
                  levelControllerMappings.length > 0) && <Arrow />}
              </>
            )}

            {/* Tower */}
            {flow.tower_id && (
              <>
                <FlowBlock
                  icon={Layers}
                  title="Tower"
                  value={label(towersData, "id", flow.tower_id)}
                  bgColor="bg-gradient-to-br from-purple-500 to-purple-600"
                />
                {assignedUsersLabel && <Arrow />}
              </>
            )}

            {assignedUsersLabel && (
              <>
                <FlowBlock
                  icon={Users}
                  title="Admins"
                  value={assignedUsersLabel}
                  bgColor="bg-gradient-to-br from-slate-600 to-slate-700"
                />
              </>
            )}
            {(flow.tower_id || assignedUsersLabel) &&
              levelControllerMappings.length > 0 && <Arrow />}

            {levelControllerMappings.length > 0 && (
              <div className="flex flex-col gap-3">
                {levelControllerMappings.map((entry) => (
                  <div
                    key={entry.level_controller_id}
                    className="flex flex-col gap-2"
                  >
                    <FlowBlock
                      icon={Network}
                      title="Level Ctrl"
                      value={getAssetName(
                        levelControllersData,
                        "device_id",
                        entry.level_controller_id,
                      )}
                      bgColor="bg-gradient-to-br from-indigo-500 to-indigo-600"
                    />
                    <div className="flex flex-col gap-2">
                      {(entry.valve_tank_mappings || []).map(
                        (mapping, index) => (
                          <MappingRow key={index} mapping={mapping} />
                        ),
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   Live preview tree while filling the form
───────────────────────────────────────────── */
const LivePreviewTree = ({
  flow,
  currentVTMapping,
  societiesData,
  adminsData,
  ugTanksData,
  towersData,
  pumpControllersData,
  borewellPumpControllersData,
  pumpsData,
  levelControllersData,
  tanksData,
  valvesData,
  sourcesData,
  flowmetersData,
}) => {
  const label = (list, idField, id, displayField = "name") => {
    if (!list || !id) return null;
    const arr = Array.isArray(list) ? list : [list];
    const found = arr.find((x) => x[idField] === id);
    return found ? found[displayField] : id;
  };
  const sourceAssetName = (sourceId, fallbackValue = "") =>
    getFirstAssetName(
      [
        { list: sourcesData, idFields: "source_id" },
        { list: tanksData, idFields: "tank_id" },
        { list: ugTanksData, idFields: "ug_tank_id" },
      ],
      sourceId,
      fallbackValue,
    );
  const destinationTankName = (tankId, fallbackValue = "") =>
    getFirstAssetName(
      [
        { list: tanksData, idFields: "tank_id" },
        { list: ugTanksData, idFields: "ug_tank_id" },
      ],
      tankId,
      fallbackValue,
    );

  const societyLabel =
    societiesData && flow.society_id
      ? societiesData.id === flow.society_id
        ? societiesData.name
        : flow.society_id
      : null;
  const assignedUserIds =
    Array.isArray(flow.user_ids) && flow.user_ids.length
      ? flow.user_ids
      : flow.user_id
        ? [flow.user_id]
        : [];
  const assignedUsersLabel = assignedUserIds
    .map((id) => {
      const admin = adminsData.find((item) => item.id === id);
      return admin?.name || admin?.email || id;
    })
    .filter(Boolean)
    .join(", ");
  const pumpControllerIds = getFlowPumpControllerIds(flow);
  const levelControllerMappings = getFlowLevelControllerMappings(flow);
  const atbpcConfig = normalizeATBPCConfig(flow.atbpc_config);
  const flowmeterLabel = (flowmeterId, displayValue = "") => {
    const found = flowmetersData.find((item) => item.id === flowmeterId);
    return (
      found?.name ||
      displayValue ||
      (found ? `Slave ${found.slave_id}` : flowmeterId)
    );
  };

  return (
    <div className="min-h-[220px] overflow-y-auto rounded-xl border border-slate-200 bg-white p-4 shadow-sm lg:h-full lg:min-h-0">
      <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-3">
        Live Preview
      </p>
      {!flow.society_id ? (
        <div className="flex min-h-40 items-center justify-center rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 text-center text-sm text-slate-500">
          Flow preview will appear as you configure the mapping.
        </div>
      ) : (
        <div className="font-mono text-sm">
          {/* Society */}
          <div className="flex items-center gap-1 text-slate-700 font-semibold">
            <Building2 className="h-4 w-4 text-blue-500" />
            <span>{societyLabel || flow.society_id}</span>
          </div>

          {atbpcConfig.atbpc_device_id && (
            <div className="ml-4 border-l-2 border-emerald-200 pl-3 mt-1">
              <div className="flex items-center gap-1 text-emerald-700">
                <Power className="h-3 w-3" />
                <span>
                  {label(
                    borewellPumpControllersData,
                    "device_id",
                    atbpcConfig.atbpc_device_id,
                  )}
                </span>
              </div>
              {(atbpcConfig.pump_configurations || []).map((mapping, index) => (
                <div
                  key={`${mapping.pump_id}-${index}`}
                  className="ml-4 border-l-2 border-sky-200 pl-3 mt-1"
                >
                  <div className="flex items-center gap-1 flex-wrap">
                    <span className="text-sky-700 flex items-center gap-1">
                      <Droplets className="h-3 w-3" />
                      {sourceAssetName(
                        mapping.source_id,
                        mapping.source_display,
                      )}
                    </span>
                    <ChevronRight className="h-3 w-3 text-slate-300" />
                    <span className="text-orange-700 flex items-center gap-1">
                      <Activity className="h-3 w-3" />
                      {getAssetName(
                        pumpsData,
                        "pump_id",
                        mapping.pump_id,
                        mapping.pump_display,
                      )}
                    </span>
                    {mapping.flowmeter_id && (
                      <>
                        <ChevronRight className="h-3 w-3 text-slate-300" />
                        <span className="text-cyan-700 flex items-center gap-1">
                          <Waves className="h-3 w-3" />
                          {flowmeterLabel(
                            mapping.flowmeter_id,
                            mapping.flowmeter_display,
                          )}
                        </span>
                      </>
                    )}
                    <ChevronRight className="h-3 w-3 text-slate-300" />
                    <span className="text-teal-700 flex items-center gap-1">
                      <Layers className="h-3 w-3" />
                      {destinationTankName(
                        mapping.destination_tank_id,
                        mapping.destination_tank_display,
                      )}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* UG Tank */}
          {flow.ug_tank_id && (
            <div className="ml-4 border-l-2 border-cyan-200 pl-3 mt-1">
              <div className="flex items-center gap-1 text-cyan-700">
                <Droplets className="h-3 w-3" />
                <span>
                  {getAssetName(ugTanksData, "ug_tank_id", flow.ug_tank_id)}
                </span>
              </div>
            </div>
          )}

          {/* Tower */}
          {flow.tower_id && (
            <div className="ml-4 border-l-2 border-purple-200 pl-3 mt-1">
              <div className="flex items-center gap-1 text-purple-700">
                <Layers className="h-3 w-3" />
                <span>{label(towersData, "id", flow.tower_id)}</span>
              </div>
            </div>
          )}

          {assignedUsersLabel && (
            <div className="ml-4 border-l-2 border-slate-200 pl-3 mt-1">
              <div className="flex items-center gap-1 text-slate-700">
                <Users className="h-3 w-3" />
                <span>{assignedUsersLabel}</span>
              </div>
            </div>
          )}

          {pumpControllerIds.length > 0 && (
            <div className="ml-4 border-l-2 border-yellow-200 pl-3 mt-1">
              {pumpControllerIds.map((controllerId) => (
                <div
                  key={controllerId}
                  className="flex items-center gap-1 text-yellow-700"
                >
                  <Zap className="h-3 w-3" />
                  <span>
                    {getAssetName(
                      pumpControllersData,
                      "device_id",
                      controllerId,
                    )}
                  </span>
                </div>
              ))}
            </div>
          )}

          {levelControllerMappings.map((entry) => (
            <div
              key={entry.level_controller_id}
              className="ml-4 border-l-2 border-indigo-200 pl-3 mt-1"
            >
              <div className="flex items-center gap-1 text-indigo-700">
                <Network className="h-3 w-3" />
                <span>
                  {getAssetName(
                    levelControllersData,
                    "device_id",
                    entry.level_controller_id,
                  )}
                </span>
              </div>

              {(entry.valve_tank_mappings || []).map((m, i) => (
                <div
                  key={i}
                  className="ml-4 border-l-2 border-pink-200 pl-3 mt-1"
                >
                  <div className="flex items-center gap-1 flex-wrap">
                    {!getMappingPumps(m).length && m.pump_controller_id && (
                      <>
                        <span className="text-yellow-700 flex items-center gap-1">
                          <Zap className="h-3 w-3" />
                          {getAssetName(
                            pumpControllersData,
                            "device_id",
                            m.pump_controller_id,
                          )}
                        </span>
                        {(m.valve_id || m.tank_id) && (
                          <ChevronRight className="h-3 w-3 text-slate-300" />
                        )}
                      </>
                    )}
                    {getMappingPumps(m).length > 0 && (
                      <>
                        <span className="flex flex-col gap-0.5">
                          {getMappingPumps(m).map((pump) => (
                            <span
                              key={pump.pump_id}
                              className="text-orange-700 flex items-center gap-1"
                            >
                              <Activity className="h-3 w-3" />
                              {getAssetName(pumpsData, "pump_id", pump.pump_id)}
                              {pump.pump_controller_id && (
                                <span className="text-yellow-700 flex items-center gap-0.5">
                                  <Zap className="h-3 w-3" />
                                  {getAssetName(
                                    pumpControllersData,
                                    "device_id",
                                    pump.pump_controller_id,
                                  )}
                                </span>
                              )}
                            </span>
                          ))}
                        </span>
                        {(m.valve_id || m.tank_id) && (
                          <ChevronRight className="h-3 w-3 text-slate-300" />
                        )}
                      </>
                    )}
                    {m.valve_id && (
                      <>
                        <span className="text-pink-700 flex items-center gap-1">
                          <FlaskConical className="h-3 w-3" />
                          {getAssetName(valvesData, "valve_id", m.valve_id)}
                        </span>
                        {m.tank_id && (
                          <ChevronRight className="h-3 w-3 text-slate-300" />
                        )}
                      </>
                    )}
                    {m.tank_id && (
                      <span className="text-teal-700 flex items-center gap-1">
                        <Waves className="h-3 w-3" />
                        {getAssetName(tanksData, "tank_id", m.tank_id)}
                      </span>
                    )}
                  </div>
                </div>
              ))}

              {currentVTMapping.level_controller_id ===
                entry.level_controller_id &&
                (currentVTMapping.valve_id ||
                  currentVTMapping.tank_id ||
                  currentVTMapping.pump_id ||
                  currentVTMapping.pump_controller_id) && (
                  <div className="ml-4 border-l-2 border-dashed border-pink-100 pl-3 mt-1 flex items-center gap-1 text-pink-400 italic flex-wrap">
                    {currentVTMapping.pump_controller_id && (
                      <>
                        <span className="flex items-center gap-1">
                          <Zap className="h-3 w-3" />
                          {getAssetName(
                            pumpControllersData,
                            "device_id",
                            currentVTMapping.pump_controller_id,
                          )}
                        </span>
                        {(currentVTMapping.pump_id ||
                          currentVTMapping.valve_id ||
                          currentVTMapping.tank_id) && (
                          <ChevronRight className="h-3 w-3 text-slate-300" />
                        )}
                      </>
                    )}
                    {currentVTMapping.pump_id && (
                      <>
                        <span className="flex items-center gap-1">
                          <Activity className="h-3 w-3" />
                          {getAssetName(
                            pumpsData,
                            "pump_id",
                            currentVTMapping.pump_id,
                          )}
                        </span>
                        {(currentVTMapping.valve_id ||
                          currentVTMapping.tank_id) && (
                          <ChevronRight className="h-3 w-3 text-slate-300" />
                        )}
                      </>
                    )}
                    {currentVTMapping.valve_id && (
                      <>
                        <span className="flex items-center gap-1">
                          <FlaskConical className="h-3 w-3" />
                          {getAssetName(
                            valvesData,
                            "valve_id",
                            currentVTMapping.valve_id,
                          )}
                        </span>
                        {currentVTMapping.tank_id && (
                          <ChevronRight className="h-3 w-3 text-slate-300" />
                        )}
                      </>
                    )}
                    {currentVTMapping.tank_id && (
                      <span className="text-teal-400 flex items-center gap-1">
                        <Waves className="h-3 w-3" />
                        {getAssetName(
                          tanksData,
                          "tank_id",
                          currentVTMapping.tank_id,
                        )}
                      </span>
                    )}
                  </div>
                )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════ */
const DeviceRegistration = () => {
  const [loading, setLoading] = useState(false);

  const generateRows = (count, template, maxRows = MAX_ROWS) => {
    const num = Number(count);
    if (!num || num <= 0) {
      toast.error("Please enter a valid count");
      return [];
    }
    if (num > maxRows) {
      toast.error(`Maximum ${maxRows} items allowed`);
      return [];
    }
    return Array.from({ length: num }, () => ({ ...template }));
  };

  /* ─── SOCIETY ─── */
  const [society, setSociety] = useState({ name: "", location: "" });
  const [editingSociety, setEditingSociety] = useState(false);

  const submitSociety = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (societiesData?.id) {
        await api.updateSociety(society);
        toast.success("Society updated successfully");
        setEditingSociety(false);
      } else {
        await api.createSociety(society);
        toast.success("Society created successfully");
      }
      setSociety({ name: "", location: "" });
      await refetchDropdownData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to save society"));
    } finally {
      setLoading(false);
    }
  };

  const startEditSociety = () => {
    if (!societiesData?.id) return;
    setSociety({
      name: societiesData.name || "",
      location: societiesData.location || "",
    });
    setEditingSociety(true);
  };

  const cancelEditSociety = () => {
    setSociety({ name: "", location: "" });
    setEditingSociety(false);
  };

  const deleteSociety = async () => {
    if (!societiesData?.id) return;
    if (
      !window.confirm(
        "Delete this society? This is only allowed when no related records exist.",
      )
    ) {
      return;
    }

    setLoading(true);
    try {
      await api.deleteSociety();
      toast.success("Society deleted successfully");
      setSociety({ name: "", location: "" });
      setEditingSociety(false);
      await refetchDropdownData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to delete society"));
    } finally {
      setLoading(false);
    }
  };

  /* ─── INFRASTRUCTURE ─── */
  const [source, setSource] = useState({
    source_id: "",
    name: "",
    type: "",
    status: "",
    sensor_type: "",
    sensor_slave_id: "",
    cable_length_m: "",
    tank_height: "",
    low: "",
    med: "",
    high: "",
    publish: false,
    location: "",
  });
  const [flowmeter, setFlowmeter] = useState({
    name: "",
    status: "",
    slave_id: "",
    totalizer: {
      addr: "",
      quantity: "",
      data_type: "",
      order: "",
      scale: "",
      offset: "",
    },
    flowrate: {
      addr: "",
      quantity: "",
      data_type: "",
      order: "",
      scale: "",
      offset: "",
    },
  });
  const [ugCount, setUgCount] = useState("");
  const [ugTanks, setUgTanks] = useState([]);
  const [towerCount, setTowerCount] = useState("");
  const [towers, setTowers] = useState([]);
  const [tankCount, setTankCount] = useState("");
  const [tanks, setTanks] = useState([]);
  const [additionalSensorCount, setAdditionalSensorCount] = useState("");
  const [additionalSensors, setAdditionalSensors] = useState([]);

  /* ─── DEVICES ─── */
  const [levelCount, setLevelCount] = useState("");
  const [levelDevices, setLevelDevices] = useState([]);
  const [pumpControllerCount, setPumpControllerCount] = useState("");
  const [pumpControllers, setPumpControllers] = useState([]);
  const [borewellPumpControllerCount, setBorewellPumpControllerCount] =
    useState("");
  const [borewellPumpControllers, setBorewellPumpControllers] = useState([]);
  const [pumpCount, setPumpCount] = useState("");
  const [pumps, setPumps] = useState([]);
  const [valveCount, setValveCount] = useState("");
  const [valves, setValves] = useState([]);

  /* ─── DROPDOWN DATA ─── */
  const [societiesData, setSocietiesData] = useState([]);
  const [adminsData, setAdminsData] = useState([]);
  const [ugTanksData, setUgTanksData] = useState([]);
  const [towersData, setTowersData] = useState([]);
  const [pumpControllersData, setPumpControllersData] = useState([]);
  const [borewellPumpControllersData, setBorewellPumpControllersData] =
    useState([]);
  const [pumpsData, setPumpsData] = useState([]);
  const [levelControllersData, setLevelControllersData] = useState([]);
  const [tanksData, setTanksData] = useState([]);
  const [valvesData, setValvesData] = useState([]);
  const [sourcesData, setSourcesData] = useState([]);
  const [flowmetersData, setFlowmetersData] = useState([]);

  /* ─── REFETCH DROPDOWN DATA ─── */
  const hasValue = (value) =>
    value !== null && value !== undefined && String(value).trim() !== "";

  const validateRows = (rows, rules, label) => {
    if (!rows.length) {
      toast.error(`Please generate at least one ${label}`);
      return false;
    }

    for (let index = 0; index < rows.length; index += 1) {
      const row = rows[index];

      for (const rule of rules) {
        const value = rule.getValue ? rule.getValue(row) : row[rule.key];

        if (!hasValue(value)) {
          toast.error(`${label} row ${index + 1}: ${rule.label} is required`);
          return false;
        }

        if (rule.type === "number") {
          const numericValue = Number(value);

          if (!Number.isFinite(numericValue)) {
            toast.error(
              `${label} row ${index + 1}: ${rule.label} must be a number`,
            );
            return false;
          }

          if (rule.integer && !Number.isInteger(numericValue)) {
            toast.error(
              `${label} row ${index + 1}: ${rule.label} must be an integer`,
            );
            return false;
          }

          if (rule.min !== undefined && numericValue < rule.min) {
            toast.error(
              `${label} row ${index + 1}: ${rule.label} must be at least ${rule.min}`,
            );
            return false;
          }

          if (rule.max !== undefined && numericValue > rule.max) {
            toast.error(
              `${label} row ${index + 1}: ${rule.label} must be at most ${rule.max}`,
            );
            return false;
          }
        }

        if (rule.allowedValues) {
          const allowedValues = rule.allowedValues.map(String);

          if (!allowedValues.includes(String(value))) {
            toast.error(
              `${label} row ${index + 1}: ${rule.label} must be one of ${rule.allowedMessage || allowedValues.join(", ")}`,
            );
            return false;
          }
        }
      }
    }

    return true;
  };

  const refetchDropdownData = async () => {
    try {
      const [
        societies,
        admins,
        ugTanks,
        towers,
        pumpControllers,
        borewellPumpControllers,
        pumps,
        levelControllers,
        tanks,
        valves,
        sources,
        flowmeters,
      ] = await Promise.all([
        api
          .getSocieties()
          .then((r) => ({ data: r.data }))
          .catch((err) => {
            toast.error(getApiErrorMessage(err, "Failed to load societies"));
            return { data: null };
          }),
        api
          .getAdminUsers()
          .then((r) => ({ data: r }))
          .catch(() => ({ data: [] })),
        api
          .getUGTanks()
          .then((r) => ({ data: r.data }))
          .catch(() => ({ data: [] })),
        api
          .getTowers()
          .then((r) => ({ data: r.data }))
          .catch(() => ({ data: [] })),
        api
          .getPumpControllers()
          .then((r) => ({ data: r.data }))
          .catch(() => ({ data: [] })),
        api
          .getBorewellPumpControllers()
          .then((r) => ({ data: r.data }))
          .catch(() => ({ data: [] })),
        api
          .getPumps()
          .then((r) => ({ data: r.data }))
          .catch(() => ({ data: [] })),
        api
          .getLevelControllers()
          .then((r) => ({ data: r.data }))
          .catch(() => ({ data: [] })),
        api
          .getTanks()
          .then((r) => ({ data: r.data }))
          .catch(() => ({ data: [] })),
        api
          .getValves()
          .then((r) => ({ data: r.data }))
          .catch(() => ({ data: [] })),
        api
          .getSources()
          .then((r) => ({ data: r.data }))
          .catch(() => ({ data: [] })),
        api
          .getFlowmeters()
          .then((r) => ({ data: r.data }))
          .catch(() => ({ data: [] })),
      ]);
      setSocietiesData(societies.data || null);
      setAdminsData(admins.data || []);
      setUgTanksData(ugTanks.data || []);
      setTowersData(towers.data || []);
      setPumpControllersData(pumpControllers.data || []);
      setBorewellPumpControllersData(borewellPumpControllers.data || []);
      setPumpsData(pumps.data || []);
      setLevelControllersData(levelControllers.data || []);
      setTanksData(tanks.data || []);
      setValvesData(valves.data || []);
      setSourcesData(sources.data || []);
      setFlowmetersData(flowmeters.data || []);
    } catch (error) {
      toast.error("Failed to load dropdown data");
    }
  };

  /* Fetch flows from API on mount */
  const fetchFlows = async () => {
    setLoadingFlows(true);
    try {
      const response = await api.getFlows();
      setSavedFlows(response || []);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to load flows"));
      setSavedFlows([]);
    } finally {
      setLoadingFlows(false);
    }
  };

  useEffect(() => {
    refetchDropdownData();
    fetchFlows();
  }, []);

  /* ─── ARCHITECTURE – FLOWS ─── */
  const [savedFlows, setSavedFlows] = useState([]);
  const [loadingFlows, setLoadingFlows] = useState(false);
  const [showFlowModal, setShowFlowModal] = useState(false);
  const [editingFlowId, setEditingFlowId] = useState(null);

  const getEmptyFlow = () => ({
    society_id: societiesData?.id || "",
    user_id: "",
    user_ids: [],
    ug_tank_id: "",
    pump_controller_id: "",
    pump_controller_ids: [],
    tower_id: "",
    level_controller_id: "",
    level_controller_mappings: [],
    valve_tank_mappings: [],
    atbpc_config: {
      atbpc_device_id: "",
      pump_configurations: [],
    },
  });

  const getEmptyVTMapping = (levelControllerId = "") => ({
    level_controller_id: levelControllerId,
    valve_id: "",
    tank_id: "",
    pump_id: "",
    pump_controller_id: "",
  });

  const [currentFlow, setCurrentFlow] = useState(getEmptyFlow);
  const [currentVTMapping, setCurrentVTMapping] = useState(getEmptyVTMapping());

  useEffect(() => {
    if (!societiesData?.id || editingFlowId) return;

    setCurrentFlow((prev) =>
      prev.society_id ? prev : { ...prev, society_id: societiesData.id },
    );
  }, [societiesData?.id, editingFlowId]);

  /* Helper function to get display labels from UUID */
  const getDisplayLabel = (list, id, displayField = "name") => {
    if (!list || !id) return "";
    const arr = Array.isArray(list) ? list : [list];
    const found = arr.find((x) => x.id === id);
    return found ? found[displayField] : id;
  };

  const getFlowUserIds = (flow) =>
    Array.isArray(flow.user_ids) && flow.user_ids.length
      ? flow.user_ids
      : flow.user_id
        ? [flow.user_id]
        : [];

  const getAdminLabel = (adminId) => {
    const admin = adminsData.find((item) => item.id === adminId);
    return admin?.name || admin?.email || adminId;
  };

  const toggleAssignedUser = (adminId) => {
    setCurrentFlow((prev) => {
      const currentUserIds = getFlowUserIds(prev);
      const user_ids = currentUserIds.includes(adminId)
        ? currentUserIds.filter((id) => id !== adminId)
        : [...currentUserIds, adminId];

      return {
        ...prev,
        user_ids,
        user_id: user_ids[0] || "",
      };
    });
  };

  /* Compute already-used IDs from saved flows (excluding current flow if editing) */
  const usedLevelControllerIds = savedFlows
    .filter((f) => f.id !== editingFlowId)
    .flatMap(getFlowLevelControllerIds)
    .filter(Boolean);
  const usedTankIds = savedFlows
    .filter((f) => f.id !== editingFlowId)
    .flatMap((f) => getFlowValveTankMappings(f).map((m) => m.tank_id))
    .filter(Boolean);

  /* Also exclude tanks already selected in the current in-progress flow.
     Valves are not excluded: one valve can feed multiple tanks. */
  const currentFlowUsedTankIds = getFlowValveTankMappings(currentFlow).map(
    (m) => m.tank_id,
  );

  const availableTowers = towersData;
  const availableValves = valvesData;
  const availableTanks = tanksData.filter(
    (t) =>
      !usedTankIds.includes(t.tank_id) &&
      !currentFlowUsedTankIds.includes(t.tank_id),
  );

  const currentPumpControllerIds = getFlowPumpControllerIds(currentFlow);
  const currentLevelControllerMappings =
    getFlowLevelControllerMappings(currentFlow);
  const currentLevelControllerIds = getFlowLevelControllerIds(currentFlow);
  const currentATBPCConfig = normalizeATBPCConfig(currentFlow.atbpc_config);
  const atbpcSourceOptions = [
    ...sourcesData.map((sourceItem) => ({
      key: `source-${sourceItem.id || sourceItem.source_id}`,
      value: sourceItem.source_id,
      label: sourceItem.name || sourceItem.source_id,
      group: sourceItem.type === 0 ? "Borewell" : "Source",
    })),
    ...tanksData.map((tank) => ({
      key: `tank-source-${tank.tank_id}`,
      value: tank.tank_id,
      label: tank.name || tank.tank_id,
      group: "Tank",
    })),
    ...ugTanksData.map((tank) => ({
      key: `ug-source-${tank.ug_tank_id}`,
      value: tank.ug_tank_id,
      label: tank.name || tank.ug_tank_id,
      group: "UG Tank",
    })),
  ].filter((option) => option.value);
  const atbpcDestinationOptions = [
    ...tanksData.map((tank) => ({
      key: `tank-destination-${tank.tank_id}`,
      value: tank.tank_id,
      label: tank.name || tank.tank_id,
      group: "Tank",
    })),
    ...ugTanksData.map((tank) => ({
      key: `ug-destination-${tank.ug_tank_id}`,
      value: tank.ug_tank_id,
      label: tank.name || tank.ug_tank_id,
      group: "UG Tank",
    })),
  ].filter((option) => option.value);
  const additionalSensorDeviceOptions = [
    ...levelControllersData.map((device) => ({
      value: device.device_id,
      label: `${device.device_id} - Level Controller`,
    })),
    ...pumpControllersData.map((device) => ({
      value: device.device_id,
      label: `${device.device_id} - Pump Controller`,
    })),
    ...borewellPumpControllersData.map((device) => ({
      value: device.device_id,
      label: `${device.device_id} - Borewell Pump Ctrl`,
    })),
  ].filter((option) => option.value);

  const togglePumpController = (controllerId) => {
    const selected = currentPumpControllerIds.includes(controllerId);
    const nextControllerIds = selected
      ? currentPumpControllerIds.filter((id) => id !== controllerId)
      : [...currentPumpControllerIds, controllerId];

    setCurrentFlow((prev) => {
      const level_controller_mappings = getFlowLevelControllerMappings(
        prev,
      ).map((entry) => ({
        ...entry,
        valve_tank_mappings: (entry.valve_tank_mappings || []).map((mapping) => {
          // Drop the removed controller from every pump on the tank.
          const pumps = getMappingPumps(mapping).map((pump) =>
            pump.pump_controller_id &&
            !nextControllerIds.includes(pump.pump_controller_id)
              ? { pump_id: pump.pump_id }
              : pump,
          );
          const { pump_controller_id, ...rest } = mapping;
          return normalizeVTMapping({
            ...rest,
            pump_controller_id:
              pump_controller_id && nextControllerIds.includes(pump_controller_id)
                ? pump_controller_id
                : "",
            pumps,
          });
        }),
      }));

      return normalizeFlowForForm(
        {
          ...prev,
          pump_controller_ids: nextControllerIds,
          pump_controller_id: nextControllerIds[0] || "",
          level_controller_mappings,
        },
        societiesData?.id,
      );
    });

    if (selected && currentVTMapping.pump_controller_id === controllerId) {
      setCurrentVTMapping((prev) => ({ ...prev, pump_controller_id: "" }));
    }
  };

  const updateATBPCConfig = (updates) => {
    setCurrentFlow((prev) =>
      normalizeFlowForForm(
        {
          ...prev,
          atbpc_config: {
            ...normalizeATBPCConfig(prev.atbpc_config),
            ...updates,
          },
        },
        societiesData?.id,
      ),
    );
  };

  const addATBPCPumpConfiguration = () => {
    setCurrentFlow((prev) => {
      const atbpc_config = normalizeATBPCConfig(prev.atbpc_config);
      return normalizeFlowForForm(
        {
          ...prev,
          atbpc_config: {
            ...atbpc_config,
            pump_configurations: [
              ...atbpc_config.pump_configurations,
              getEmptyATBPCPumpConfiguration(),
            ],
          },
        },
        societiesData?.id,
      );
    });
  };

  const updateATBPCPumpConfiguration = (index, updates) => {
    setCurrentFlow((prev) => {
      const atbpc_config = normalizeATBPCConfig(prev.atbpc_config);
      return normalizeFlowForForm(
        {
          ...prev,
          atbpc_config: {
            ...atbpc_config,
            pump_configurations: atbpc_config.pump_configurations.map(
              (mapping, mappingIndex) =>
                mappingIndex === index ? { ...mapping, ...updates } : mapping,
            ),
          },
        },
        societiesData?.id,
      );
    });
  };

  const removeATBPCPumpConfiguration = (index) => {
    setCurrentFlow((prev) => {
      const atbpc_config = normalizeATBPCConfig(prev.atbpc_config);
      return normalizeFlowForForm(
        {
          ...prev,
          atbpc_config: {
            ...atbpc_config,
            pump_configurations: atbpc_config.pump_configurations.filter(
              (_, mappingIndex) => mappingIndex !== index,
            ),
          },
        },
        societiesData?.id,
      );
    });
  };

  const toggleLevelController = (controllerId) => {
    const selected = currentLevelControllerIds.includes(controllerId);

    setCurrentFlow((prev) => {
      const existingEntries = getFlowLevelControllerMappings(prev);
      const level_controller_mappings = selected
        ? existingEntries.filter(
            (entry) => entry.level_controller_id !== controllerId,
          )
        : [
            ...existingEntries,
            { level_controller_id: controllerId, valve_tank_mappings: [] },
          ];

      return normalizeFlowForForm(
        {
          ...prev,
          level_controller_mappings,
        },
        societiesData?.id,
      );
    });

    if (selected && currentVTMapping.level_controller_id === controllerId) {
      setCurrentVTMapping(getEmptyVTMapping());
    }
  };

  const updateCurrentMapping = (levelControllerId, updates) => {
    setCurrentVTMapping((prev) => ({
      ...(prev.level_controller_id === levelControllerId
        ? prev
        : getEmptyVTMapping(levelControllerId)),
      ...updates,
      level_controller_id: levelControllerId,
    }));
  };

  const addVTMapping = (levelControllerId) => {
    const mappingDraft =
      currentVTMapping.level_controller_id === levelControllerId
        ? currentVTMapping
        : getEmptyVTMapping(levelControllerId);
    const { valve_id, tank_id, pump_id } = mappingDraft;
    let pumpControllerId = mappingDraft.pump_controller_id;

    if (!valve_id && !tank_id) {
      toast.error("Please select at least a valve or a tank");
      return;
    }
    if (pump_id && currentPumpControllerIds.length > 1 && !pumpControllerId) {
      toast.error("Select the pump controller that owns this pump");
      return;
    }
    if (pump_id && currentPumpControllerIds.length === 1 && !pumpControllerId) {
      pumpControllerId = currentPumpControllerIds[0];
    }

    const draftPump = pump_id
      ? pumpControllerId
        ? { pump_id, pump_controller_id: pumpControllerId }
        : { pump_id }
      : null;

    // A tank can be fed by several pumps: picking an already-mapped tank adds
    // the pump to that tank's mapping instead of creating a duplicate row.
    const currentEntry = getFlowLevelControllerMappings(currentFlow).find(
      (entry) => entry.level_controller_id === levelControllerId,
    );
    const existingIndex = tank_id
      ? (currentEntry?.valve_tank_mappings || []).findIndex(
          (mapping) => mapping.tank_id === tank_id,
        )
      : -1;

    if (existingIndex >= 0) {
      const existing = currentEntry.valve_tank_mappings[existingIndex];
      const existingPumps = getMappingPumps(existing);
      if (valve_id && existing.valve_id && existing.valve_id !== valve_id) {
        toast.error(
          "This tank is already mapped with a different valve. Remove that mapping first.",
        );
        return;
      }
      if (!draftPump) {
        toast.error("This tank is already mapped. Select a pump to connect another pump.");
        return;
      }
      if (existingPumps.some((pump) => pump.pump_id === draftPump.pump_id)) {
        toast.error("This pump is already connected to the tank");
        return;
      }
    }

    setCurrentFlow((prev) =>
      normalizeFlowForForm(
        {
          ...prev,
          level_controller_mappings: getFlowLevelControllerMappings(prev).map(
            (entry) => {
              if (entry.level_controller_id !== levelControllerId) return entry;
              const mappings = entry.valve_tank_mappings || [];
              if (existingIndex >= 0) {
                return {
                  ...entry,
                  valve_tank_mappings: mappings.map((mapping, index) =>
                    index === existingIndex
                      ? normalizeVTMapping({
                          ...mapping,
                          valve_id: mapping.valve_id || valve_id,
                          pumps: [...getMappingPumps(mapping), draftPump],
                        })
                      : mapping,
                  ),
                };
              }
              return {
                ...entry,
                valve_tank_mappings: [
                  ...mappings,
                  normalizeVTMapping({
                    valve_id,
                    tank_id,
                    pumps: draftPump ? [draftPump] : [],
                  }),
                ],
              };
            },
          ),
        },
        societiesData?.id,
      ),
    );
    setCurrentVTMapping(getEmptyVTMapping(levelControllerId));
    toast.success(
      existingIndex >= 0 ? "Pump added to the tank's mapping" : "Mapping added",
    );
  };

  const removeMappingPump = (levelControllerId, mappingIndex, pumpId) => {
    setCurrentFlow((prev) =>
      normalizeFlowForForm(
        {
          ...prev,
          level_controller_mappings: getFlowLevelControllerMappings(prev).map(
            (entry) =>
              entry.level_controller_id === levelControllerId
                ? {
                    ...entry,
                    valve_tank_mappings: (entry.valve_tank_mappings || []).map(
                      (mapping, index) => {
                        if (index !== mappingIndex) return mapping;
                        const pumps = getMappingPumps(mapping).filter(
                          (pump) => pump.pump_id !== pumpId,
                        );
                        const { pump_id, pump_controller_id, ...rest } = mapping;
                        return normalizeVTMapping({ ...rest, pumps });
                      },
                    ),
                  }
                : entry,
          ),
        },
        societiesData?.id,
      ),
    );
  };

  const removeVTMapping = (levelControllerId, index) => {
    setCurrentFlow((prev) =>
      normalizeFlowForForm(
        {
          ...prev,
          level_controller_mappings: getFlowLevelControllerMappings(prev).map(
            (entry) =>
              entry.level_controller_id === levelControllerId
                ? {
                    ...entry,
                    valve_tank_mappings: (
                      entry.valve_tank_mappings || []
                    ).filter((_, i) => i !== index),
                  }
                : entry,
          ),
        },
        societiesData?.id,
      ),
    );
  };

  const saveFlow = async () => {
    const normalizedFlow = normalizeFlowForForm(currentFlow, societiesData?.id);
    const { society_id, ug_tank_id, tower_id, user_id, user_ids } =
      normalizedFlow;
    const pump_controller_ids = getFlowPumpControllerIds(normalizedFlow);
    const level_controller_mappings =
      getFlowLevelControllerMappings(normalizedFlow);
    const level_controller_ids = getFlowLevelControllerIds(normalizedFlow);
    const valve_tank_mappings = getFlowValveTankMappings(normalizedFlow);
    const atbpc_config = normalizeATBPCConfig(normalizedFlow.atbpc_config);
    const hasATBPCConfig =
      Boolean(atbpc_config.atbpc_device_id) ||
      atbpc_config.pump_configurations.length > 0;
    const assignedUserIds = user_ids?.length
      ? user_ids
      : user_id
        ? [user_id]
        : [];
    const configuredSocietyId = society_id || societiesData?.id || "";

    /* Validate required fields */
    if (!configuredSocietyId) {
      toast.error("Society configuration is not available yet");
      return;
    }
    if (assignedUserIds.length === 0) {
      toast.error("Please assign at least one user");
      return;
    }

    if (
      !hasATBPCConfig &&
      pump_controller_ids.length === 0 &&
      level_controller_ids.length === 0
    ) {
      toast.error(
        "Please select at least one ATBPC, pump controller, or level controller",
      );
      return;
    }

    if (hasATBPCConfig) {
      if (!atbpc_config.atbpc_device_id) {
        toast.error("Please select a borewell pump controller in ATBPC Conf.");
        return;
      }
      if (atbpc_config.pump_configurations.length === 0) {
        toast.error("Please add at least one ATBPC pump configuration");
        return;
      }

      const usedFlowmeters = new Set();
      for (
        let index = 0;
        index < atbpc_config.pump_configurations.length;
        index += 1
      ) {
        const mapping = atbpc_config.pump_configurations[index];
        const rowNumber = index + 1;
        if (
          !mapping.pump_id ||
          !mapping.destination_tank_id ||
          !mapping.source_id
        ) {
          toast.error(`ATBPC pump configuration ${rowNumber} is incomplete`);
          return;
        }
        if (mapping.flowmeter_id && usedFlowmeters.has(mapping.flowmeter_id)) {
          toast.error(
            `Flowmeter ${mapping.flowmeter_id} is mapped more than once`,
          );
          return;
        }
        if (mapping.flowmeter_id) usedFlowmeters.add(mapping.flowmeter_id);
      }
    }

    const emptyLevelController = level_controller_mappings.find(
      (entry) =>
        entry.level_controller_id &&
        (entry.valve_tank_mappings || []).length === 0,
    );
    if (emptyLevelController) {
      toast.error(
        `Please add at least one mapping for level controller ${emptyLevelController.level_controller_id}`,
      );
      return;
    }

    setLoading(true);
    try {
      const flowPayload = {
        society_id: configuredSocietyId,
        tower_id: tower_id || "",
        user_ids: assignedUserIds,
        user_id: assignedUserIds[0] || "",
        ug_tank_id: ug_tank_id || "",
        pump_controller_ids,
        pump_controller_id: pump_controller_ids[0] || "",
        level_controller_mappings,
        level_controller_id: level_controller_ids[0] || "",
        valve_tank_mappings,
        atbpc_config: hasATBPCConfig ? atbpc_config : null,
      };

      if (editingFlowId) {
        await api.updateFlow(editingFlowId, flowPayload);
        toast.success("Flow updated successfully");
      } else {
        await api.createFlow(flowPayload);
        toast.success("Flow created successfully");
      }

      /* Refresh flows list */
      await fetchFlows();

      /* Reset form */
      setCurrentFlow(getEmptyFlow());
      setCurrentVTMapping(getEmptyVTMapping());
      setEditingFlowId(null);
      setShowFlowModal(false);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to save flow"));
    } finally {
      setLoading(false);
    }
  };

  const openEditFlow = (flow) => {
    setEditingFlowId(flow.id);
    const user_ids = getFlowUserIds(flow);
    setCurrentFlow(
      normalizeFlowForForm(
        {
          ...flow,
          society_id: flow.society_id || societiesData?.id || "",
          user_ids,
          user_id: user_ids[0] || "",
        },
        societiesData?.id,
      ),
    );
    setCurrentVTMapping(getEmptyVTMapping());
    setShowFlowModal(true);
  };

  const openNewFlow = () => {
    setEditingFlowId(null);
    setCurrentFlow(getEmptyFlow());
    setCurrentVTMapping(getEmptyVTMapping());
    setShowFlowModal(true);
  };

  const deleteFlow = async (id) => {
    if (
      window.confirm(
        "Are you sure you want to delete this flow? This action cannot be undone.",
      )
    ) {
      setLoading(true);
      try {
        await api.deleteFlow(id);
        toast.success("Flow deleted successfully");
        /* Refresh flows list */
        await fetchFlows();
      } catch (error) {
        toast.error(getApiErrorMessage(error, "Failed to delete flow"));
      } finally {
        setLoading(false);
      }
    }
  };

  const closeModal = () => {
    setShowFlowModal(false);
    setCurrentFlow(getEmptyFlow());
    setCurrentVTMapping(getEmptyVTMapping());
    setEditingFlowId(null);
  };

  /* ─── BULK SUBMIT HELPERS ─── */
  const submitSource = async () => {
    const sourceId = String(source.source_id || "").trim();
    const sourceName = String(source.name || "").trim();
    const sourceType = String(source.type || "").trim();

    if (!sourceId) {
      toast.error("Please enter a source ID");
      return;
    }
    if (!sourceName) {
      toast.error("Please enter a source name");
      return;
    }
    if (!sourceType) {
      toast.error("Please select a source type");
      return;
    }
    if (!["0", "1"].includes(sourceType)) {
      toast.error("Source type must be borewell or tank");
      return;
    }
    const numericFields = [
      { key: "status", label: "Status", integer: true, min: 0, max: 1 },
      {
        key: "sensor_type",
        label: "Sensor Type",
        integer: true,
        min: 1,
        max: 4,
      },
      {
        key: "sensor_slave_id",
        label: "Sensor Slave ID",
        integer: true,
        min: 0,
        max: 500,
      },
      { key: "cable_length_m", label: "Cable Length", min: 0 },
      { key: "tank_height", label: "Tank Height", min: 0 },
      { key: "low", label: "Low", integer: true, min: 0, max: 100 },
      { key: "med", label: "Mid", integer: true, min: 0, max: 100 },
      { key: "high", label: "High", integer: true, min: 0, max: 100 },
    ];
    for (const field of numericFields) {
      const value = source[field.key];
      if (value === "" || value === null || value === undefined) {
        toast.error(`${field.label} is required`);
        return;
      }
      const numericValue = Number(value);
      if (!Number.isFinite(numericValue)) {
        toast.error(`${field.label} must be a number`);
        return;
      }
      if (field.integer && !Number.isInteger(numericValue)) {
        toast.error(`${field.label} must be an integer`);
        return;
      }
      if (field.min !== undefined && numericValue < field.min) {
        toast.error(`${field.label} must be at least ${field.min}`);
        return;
      }
      if (field.max !== undefined && numericValue > field.max) {
        toast.error(`${field.label} must be at most ${field.max}`);
        return;
      }
    }

    setLoading(true);
    try {
      await api.createSource({
        ...source,
        source_id: sourceId,
        name: sourceName,
        type: Number(sourceType),
        status: Number(source.status),
        sensor_type: Number(source.sensor_type),
        sensor_slave_id: Number(source.sensor_slave_id),
        cable_length_m: Number(source.cable_length_m),
        tank_height: Number(source.tank_height),
        low: Number(source.low),
        med: Number(source.med),
        high: Number(source.high),
      });
      toast.success(
        "Source created successfully. You can view it on Components.",
      );
      setSource({
        source_id: "",
        name: "",
        type: "",
        status: "",
        sensor_type: "",
        sensor_slave_id: "",
        cable_length_m: "",
        tank_height: "",
        low: "",
        med: "",
        high: "",
        publish: false,
        location: "",
      });
      await refetchDropdownData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to create source"));
    } finally {
      setLoading(false);
    }
  };

  const submitFlowmeter = async () => {
    const flowmeterName = String(flowmeter.name || "").trim();
    if (!flowmeterName) {
      toast.error("Please enter a flowmeter name");
      return;
    }

    const numericFields = [
      { path: ["status"], label: "Status", integer: true, min: 0 },
      {
        path: ["slave_id"],
        label: "Slave ID",
        integer: true,
        min: 0,
        max: 500,
      },
      {
        path: ["totalizer", "addr"],
        label: "Totalizer Address",
        integer: true,
        min: 0,
      },
      {
        path: ["totalizer", "quantity"],
        label: "Totalizer Quantity",
        integer: true,
        min: 0,
      },
      {
        path: ["totalizer", "data_type"],
        label: "Totalizer Data Type",
        integer: true,
        min: 1,
        max: 6,
      },
      {
        path: ["totalizer", "order"],
        label: "Totalizer Order",
        integer: true,
        min: 0,
        max: 4,
      },
      { path: ["totalizer", "scale"], label: "Totalizer Scale" },
      { path: ["totalizer", "offset"], label: "Totalizer Offset" },
      {
        path: ["flowrate", "addr"],
        label: "Flowrate Address",
        integer: true,
        min: 0,
      },
      {
        path: ["flowrate", "quantity"],
        label: "Flowrate Quantity",
        integer: true,
        min: 0,
      },
      {
        path: ["flowrate", "data_type"],
        label: "Flowrate Data Type",
        integer: true,
        min: 1,
        max: 6,
      },
      {
        path: ["flowrate", "order"],
        label: "Flowrate Order",
        integer: true,
        min: 0,
        max: 4,
      },
      { path: ["flowrate", "scale"], label: "Flowrate Scale" },
      { path: ["flowrate", "offset"], label: "Flowrate Offset" },
    ];

    for (const field of numericFields) {
      const value = field.path.reduce((item, key) => item?.[key], flowmeter);
      if (value === "" || value === null || value === undefined) {
        toast.error(`${field.label} is required`);
        return;
      }
      const numericValue = Number(value);
      if (!Number.isFinite(numericValue)) {
        toast.error(`${field.label} must be a number`);
        return;
      }
      if (field.integer && !Number.isInteger(numericValue)) {
        toast.error(`${field.label} must be an integer`);
        return;
      }
      if (field.min !== undefined && numericValue < field.min) {
        toast.error(`${field.label} must be at least ${field.min}`);
        return;
      }
      if (field.max !== undefined && numericValue > field.max) {
        toast.error(`${field.label} must be at most ${field.max}`);
        return;
      }
    }

    const normalizeRegister = (register) => ({
      addr: Number(register.addr),
      quantity: Number(register.quantity),
      data_type: Number(register.data_type),
      order: Number(register.order),
      scale: Number(register.scale),
      offset: Number(register.offset),
    });

    setLoading(true);
    try {
      await api.createFlowmeter({
        name: flowmeterName,
        status: Number(flowmeter.status),
        slave_id: Number(flowmeter.slave_id),
        totalizer: normalizeRegister(flowmeter.totalizer),
        flowrate: normalizeRegister(flowmeter.flowrate),
      });
      toast.success("Flowmeter created successfully");
      setFlowmeter({
        name: "",
        status: "",
        slave_id: "",
        totalizer: {
          addr: "",
          quantity: "",
          data_type: "",
          order: "",
          scale: "",
          offset: "",
        },
        flowrate: {
          addr: "",
          quantity: "",
          data_type: "",
          order: "",
          scale: "",
          offset: "",
        },
      });
      await refetchDropdownData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to create flowmeter"));
    } finally {
      setLoading(false);
    }
  };

  const submitBulkUGTanks = async (ugTanks) => {
    if (
      !validateRows(
        ugTanks,
        [
          { key: "ug_tank_id", label: "UG Tank ID" },
          { key: "name", label: "Name" },
          { key: "capacity", label: "Capacity", type: "number" },
          { key: "height", label: "Height", type: "number" },
          {
            key: "low",
            label: "Low",
            type: "number",
            integer: true,
            min: 0,
            max: 100,
          },
          {
            key: "med",
            label: "Mid",
            type: "number",
            integer: true,
            min: 0,
            max: 100,
          },
          {
            key: "high",
            label: "High",
            type: "number",
            integer: true,
            min: 0,
            max: 100,
          },
          { key: "sensor_type", label: "Sensor Type" },
          {
            key: "sensor_slave_id",
            label: "Sensor Slave ID",
            type: "number",
            min: 1,
            max: 500,
          },
          { key: "bme_offset", label: "BME Offset", type: "number" },
          { key: "publish", label: "Publish" },
        ],
        "UG tank",
      )
    ) {
      return;
    }

    const seenUgTankIds = new Set();
    const duplicateUgTank = ugTanks.find((tank) => {
      const normalizedId = String(tank.ug_tank_id || "")
        .trim()
        .toLowerCase();
      if (seenUgTankIds.has(normalizedId)) {
        return true;
      }
      seenUgTankIds.add(normalizedId);
      return false;
    });
    const duplicateUgTankId = duplicateUgTank?.ug_tank_id;
    if (duplicateUgTankId) {
      toast.error(
        `UG tank ID "${duplicateUgTankId}" is duplicated in this batch`,
      );
      return;
    }

    const existingUgTankIds = new Set(
      ugTanksData.map((tank) =>
        String(tank.ug_tank_id || "")
          .trim()
          .toLowerCase(),
      ),
    );
    const existingUgTank = ugTanks.find((tank) =>
      existingUgTankIds.has(
        String(tank.ug_tank_id || "")
          .trim()
          .toLowerCase(),
      ),
    );
    if (existingUgTank) {
      toast.error(`UG tank ID "${existingUgTank.ug_tank_id}" already exists`);
      return;
    }

    setLoading(true);
    try {
      await api.createUGTanks(ugTanks);
      toast.success("UG tanks created successfully");
      setUgTanks([]);
      setUgCount("");
      await refetchDropdownData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to create UG Tanks"));
    } finally {
      setLoading(false);
    }
  };

  const submitBulkTowers = async (towers) => {
    if (
      !validateRows(
        towers,
        [
          { key: "name", label: "Name" },
          { key: "location", label: "Location" },
        ],
        "tower",
      )
    ) {
      return;
    }

    setLoading(true);
    try {
      await api.createTowers(towers);
      toast.success("Towers created successfully");
      setTowers([]);
      setTowerCount("");
      await refetchDropdownData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to create towers"));
    } finally {
      setLoading(false);
    }
  };

  const submitBulkTanks = async (tanks) => {
    if (
      !validateRows(
        tanks,
        [
          { key: "tank_id", label: "Tank ID" },
          { key: "name", label: "Name" },
          { key: "capacity", label: "Capacity", type: "number" },
          { key: "low", label: "Low", type: "number", min: 0, max: 100 },
          { key: "med", label: "Mid", type: "number", min: 0, max: 100 },
          { key: "high", label: "High", type: "number", min: 0, max: 100 },
          { key: "sensor_type", label: "Sensor Type" },
          {
            key: "sensor_slave_id",
            label: "Sensor Slave ID",
            type: "number",
            min: 1,
            max: 500,
          },
          { key: "bme_offset", label: "BME Offset", type: "number" },
          { key: "publish", label: "Publish" },
          {
            key: "cluster_count",
            label: "Cluster Count",
            type: "number",
            integer: true,
            min: 0,
          },
          {
            key: "cluster_slave_id_start",
            label: "Cluster Slave ID Start",
            type: "number",
            integer: true,
            min: 0,
            max: 500,
          },
        ],
        "tank",
      )
    ) {
      return;
    }

    const seenTankIds = new Set();
    const duplicateTank = tanks.find((tank) => {
      const normalizedId = String(tank.tank_id || "")
        .trim()
        .toLowerCase();
      if (seenTankIds.has(normalizedId)) {
        return true;
      }
      seenTankIds.add(normalizedId);
      return false;
    });
    const duplicateTankId = duplicateTank?.tank_id;
    if (duplicateTankId) {
      toast.error(`Tank ID "${duplicateTankId}" is duplicated in this batch`);
      return;
    }

    const existingTankIds = new Set(
      tanksData.map((tank) =>
        String(tank.tank_id || "")
          .trim()
          .toLowerCase(),
      ),
    );
    const existingTank = tanks.find((tank) =>
      existingTankIds.has(
        String(tank.tank_id || "")
          .trim()
          .toLowerCase(),
      ),
    );
    if (existingTank) {
      toast.error(
        `Tank ID "${existingTank.tank_id}" already exists in this society`,
      );
      return;
    }

    setLoading(true);
    try {
      await api.createBulkTanks(tanks);
      toast.success("Tanks created successfully");
      setTanks([]);
      setTankCount("");
      await refetchDropdownData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to create tanks"));
    } finally {
      setLoading(false);
    }
  };

  const submitBulkAdditionalSensors = async (sensors) => {
    if (additionalSensorDeviceOptions.length === 0) {
      toast.error("Please create a controller device before adding sensors");
      return;
    }

    if (
      !validateRows(
        sensors,
        [
          { key: "s_id", label: "Sensor ID" },
          { key: "sensor_name", label: "Sensor Name" },
          { key: "device_id", label: "Device ID" },
          {
            key: "slave_id",
            label: "Slave ID",
            type: "number",
            integer: true,
            min: 1,
            max: 500,
          },
          {
            key: "start_address",
            label: "Start Address",
            type: "number",
            integer: true,
            min: 0,
          },
          {
            key: "quantity",
            label: "Quantity",
            type: "number",
            integer: true,
            min: 1,
          },
          {
            key: "data_type",
            label: "Data Type",
            type: "number",
            integer: true,
            min: 1,
            max: 6,
          },
          {
            key: "order",
            label: "Order",
            type: "number",
            integer: true,
            min: 0,
            max: 4,
          },
          { key: "scale", label: "Scale", type: "number" },
          { key: "offset", label: "Offset", type: "number" },
        ],
        "additional sensor",
      )
    ) {
      return;
    }

    const availableDeviceIds = new Set(
      additionalSensorDeviceOptions.map((option) => option.value),
    );
    const seenSensorKeys = new Set();

    for (const sensor of sensors) {
      const deviceId = String(sensor.device_id || "").trim();
      const sensorId = String(sensor.s_id || "").trim();

      if (!availableDeviceIds.has(deviceId)) {
        toast.error(`Device ID "${deviceId}" is not available in this society`);
        return;
      }

      const duplicateKey = `${deviceId.toLowerCase()}::${sensorId.toLowerCase()}`;
      if (seenSensorKeys.has(duplicateKey)) {
        toast.error(
          `Sensor ID "${sensorId}" is duplicated for device "${deviceId}" in this batch`,
        );
        return;
      }
      seenSensorKeys.add(duplicateKey);
    }

    const payload = sensors.map((sensor) => ({
      s_id: String(sensor.s_id || "").trim(),
      sensor_name: String(sensor.sensor_name || "").trim(),
      unit: String(sensor.unit || "").trim(),
      device_id: String(sensor.device_id || "").trim(),
      slave_id: Number(sensor.slave_id),
      start_address: Number(sensor.start_address),
      quantity: Number(sensor.quantity),
      data_type: Number(sensor.data_type),
      order: Number(sensor.order),
      scale: Number(sensor.scale),
      offset: Number(sensor.offset),
    }));

    setLoading(true);
    try {
      await api.createBulkAdditionalSensors(payload);
      toast.success("Additional sensors created successfully");
      setAdditionalSensors([]);
      setAdditionalSensorCount("");
      await refetchDropdownData();
    } catch (error) {
      toast.error(
        getApiErrorMessage(error, "Failed to create additional sensors"),
      );
    } finally {
      setLoading(false);
    }
  };

  const submitBulkLevelControllers = async (levelDevices) => {
    if (
      !validateRows(
        levelDevices,
        [
          { key: "device_id", label: "Device ID" },
          { key: "name", label: "Name" },
          {
            label: "Connection Type",
            type: "number",
            min: 1,
            max: 2,
            getValue: (row) => row.network?.connectionType,
          },
          {
            label: "GSM ISP",
            type: "number",
            allowedValues: GSM_APN_OPTIONS.map((option) => option.value),
            allowedMessage: GSM_APN_OPTIONS.map((option) => option.label).join(
              ", ",
            ),
            getValue: (row) => row.network?.gsmISP,
          },
        ],
        "level controller",
      )
    ) {
      return;
    }

    setLoading(true);
    try {
      await api.createBulkLevelControllers(levelDevices);
      toast.success("Level controllers created successfully");
      setLevelDevices([]);
      setLevelCount("");
      await refetchDropdownData();
    } catch (error) {
      toast.error(
        getApiErrorMessage(error, "Failed to create level controllers"),
      );
    } finally {
      setLoading(false);
    }
  };

  const submitBulkPumpControllers = async (pumpControllers) => {
    if (
      !validateRows(
        pumpControllers,
        [
          { key: "device_id", label: "Device ID" },
          { key: "name", label: "Name" },
          {
            label: "Connection Type",
            type: "number",
            min: 1,
            max: 2,
            getValue: (row) => row.network?.connectionType,
          },
          {
            label: "GSM ISP",
            type: "number",
            allowedValues: GSM_APN_OPTIONS.map((option) => option.value),
            allowedMessage: GSM_APN_OPTIONS.map((option) => option.label).join(
              ", ",
            ),
            getValue: (row) => row.network?.gsmISP,
          },
        ],
        "pump controller",
      )
    ) {
      return;
    }

    setLoading(true);
    try {
      await api.createBulkPumpControllers(pumpControllers);
      toast.success("Pump controllers created successfully");
      setPumpControllers([]);
      setPumpControllerCount("");
      await refetchDropdownData();
    } catch (error) {
      toast.error(
        getApiErrorMessage(error, "Failed to create pump controllers"),
      );
    } finally {
      setLoading(false);
    }
  };

  const submitBulkBorewellPumpControllers = async (controllers) => {
    if (
      !validateRows(
        controllers,
        [
          { key: "device_id", label: "Device ID" },
          { key: "name", label: "Name" },
          {
            label: "Connection Type",
            type: "number",
            min: 1,
            max: 2,
            getValue: (row) => row.network?.connectionType,
          },
          {
            label: "GSM ISP",
            type: "number",
            allowedValues: GSM_APN_OPTIONS.map((option) => option.value),
            allowedMessage: GSM_APN_OPTIONS.map((option) => option.label).join(
              ", ",
            ),
            getValue: (row) => row.network?.gsmISP,
          },
        ],
        "borewell pump controller",
      )
    ) {
      return;
    }

    setLoading(true);
    try {
      await api.createBulkBorewellPumpControllers(controllers);
      toast.success("Borewell pump controllers created successfully");
      setBorewellPumpControllers([]);
      setBorewellPumpControllerCount("");
      await refetchDropdownData();
    } catch (error) {
      toast.error(
        getApiErrorMessage(error, "Failed to create borewell pump controllers"),
      );
    } finally {
      setLoading(false);
    }
  };

  const submitBulkPumps = async (pumps) => {
    const basePumpRules = [
      { key: "pump_id", label: "Pump ID" },
      { key: "name", label: "Name" },
      { key: "power_rating", label: "Power Rating", type: "number" },
      {
        label: "Energy Meter Status",
        type: "number",
        min: 0,
        max: 1,
        getValue: (row) => row.energymeter?.status,
      },
      {
        label: "Energy Meter Make",
        type: "number",
        min: 0,
        max: 1,
        allowedValues: ENERGY_METER_MAKE_OPTIONS.map((option) => option.value),
        allowedMessage: ENERGY_METER_MAKE_OPTIONS.map(
          (option) => option.label,
        ).join(", "),
        getValue: (row) => row.energymeter?.make,
      },
      {
        label: "Energy Meter Slave ID",
        type: "number",
        min: 1,
        max: 500,
        getValue: (row) => row.energymeter?.slave_id,
      },
      {
        key: "control_type",
        label: "Control Type",
        type: "number",
        integer: true,
        min: 0,
        max: 3,
        allowedValues: [0, 1, 2, 3],
        allowedMessage: "0, 1, 2 or 3",
      },
    ];
    const vfdPumpRules = [
      {
        label: "VFD Slave ID",
        type: "number",
        integer: true,
        min: 0,
        max: 500,
        getValue: (row) => row.vfd?.slave_id,
      },
      {
        label: "VFD Pressure Slave ID",
        type: "number",
        integer: true,
        min: 0,
        max: 500,
        getValue: (row) => row.vfd?.pressure_slave_id,
      },
      {
        label: "Pressure Start Address",
        type: "number",
        integer: true,
        min: 0,
        getValue: (row) => row.vfd?.pressure_start_address,
      },
      {
        label: "Pressure Quantity",
        type: "number",
        integer: true,
        min: 0,
        getValue: (row) => row.vfd?.pressure_quantity,
      },
      {
        label: "Pressure Data Type",
        type: "number",
        integer: true,
        min: 1,
        max: 6,
        getValue: (row) => row.vfd?.pressure_data_type,
      },
      {
        label: "Pressure Order",
        type: "number",
        integer: true,
        min: 0,
        max: 4,
        getValue: (row) => row.vfd?.pressure_order,
      },
      {
        label: "Pressure Scale",
        type: "number",
        getValue: (row) => row.vfd?.pressure_scale,
      },
      {
        label: "Pressure Offset",
        type: "number",
        getValue: (row) => row.vfd?.pressure_offset,
      },
      {
        label: "Range Low",
        type: "number",
        getValue: (row) => row.vfd?.range_low_bar,
      },
      {
        label: "Range High",
        type: "number",
        getValue: (row) => row.vfd?.range_high_bar,
      },
      {
        label: "Min Frequency",
        type: "number",
        getValue: (row) => row.vfd?.min_freq_hz,
      },
      {
        label: "Max Frequency",
        type: "number",
        getValue: (row) => row.vfd?.max_freq_hz,
      },
    ];
    const pressureRangePumpRules = [
      {
        label: "Pressure Range PT ID",
        getValue: (row) => row.pressure_range?.pt_id,
      },
      {
        label: "Pressure Stage Index",
        type: "number",
        integer: true,
        min: 0,
        getValue: (row) => row.pressure_range?.stage_index,
      },
      {
        label: "Pressure PT Slave ID",
        type: "number",
        integer: true,
        min: 0,
        max: 500,
        getValue: (row) => row.pressure_range?.pt_slave_id,
      },
      {
        label: "Pressure PT Start Address",
        type: "number",
        integer: true,
        min: 0,
        getValue: (row) => row.pressure_range?.pt_start_address,
      },
      {
        label: "Pressure PT Quantity",
        type: "number",
        integer: true,
        min: 0,
        getValue: (row) => row.pressure_range?.pt_quantity,
      },
      {
        label: "Pressure PT Data Type",
        type: "number",
        integer: true,
        min: 0,
        getValue: (row) => row.pressure_range?.pt_data_type,
      },
      {
        label: "Pressure PT Order",
        type: "number",
        integer: true,
        min: 0,
        getValue: (row) => row.pressure_range?.pt_order,
      },
      {
        label: "Pressure PT Scale",
        type: "number",
        getValue: (row) => row.pressure_range?.pt_scale,
      },
      {
        label: "Pressure PT Offset",
        type: "number",
        getValue: (row) => row.pressure_range?.pt_offset,
      },
      {
        label: "Pressure Range Low Bar",
        type: "number",
        getValue: (row) => row.pressure_range?.range_low_bar,
      },
      {
        label: "Pressure Range High Bar",
        type: "number",
        getValue: (row) => row.pressure_range?.range_high_bar,
      },
    ];

    if (!validateRows(pumps, basePumpRules, "pump")) {
      return;
    }

    for (const pump of pumps) {
      const controlType = Number(pump.control_type);
      const rules =
        isVfdPumpControlType(controlType)
          ? vfdPumpRules
          : controlType === 2
            ? pressureRangePumpRules
            : [];
      if (rules.length > 0 && !validateRows([pump], rules, "pump")) {
        return;
      }
    }

    const normalizedPumps = pumps.map((pump) => {
      const controlType = Number(pump.control_type);
      const normalized = {
        ...pump,
        energymeter: {
          ...pump.energymeter,
          make: Number(normalizeEnergyMeterMake(pump.energymeter?.make)),
        },
        control_type: controlType,
      };

      if (isVfdPumpControlType(controlType)) {
        delete normalized.pressure_range;
      } else if (controlType === 2) {
        delete normalized.vfd;
      } else {
        delete normalized.vfd;
        delete normalized.pressure_range;
      }

      return normalized;
    });

    setLoading(true);
    try {
      await api.createBulkPumps(normalizedPumps);
      toast.success("Pumps created successfully");
      setPumps([]);
      setPumpCount("");
      await refetchDropdownData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to create pumps"));
    } finally {
      setLoading(false);
    }
  };

  const submitBulkValves = async (valves) => {
    if (
      !validateRows(
        valves,
        [
          { key: "valve_id", label: "Valve ID" },
          { key: "type", label: "Valve Type" },
        ],
        "valve",
      )
    ) {
      return;
    }

    setLoading(true);
    try {
      await api.createBulkValves(valves);
      toast.success("Valves created successfully");
      setValves([]);
      setValveCount("");
      await refetchDropdownData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to create valves"));
    } finally {
      setLoading(false);
    }
  };

  /* ─── SHARED PROPS FOR TREE COMPONENTS ─── */
  const treeProps = {
    societiesData,
    adminsData,
    ugTanksData,
    towersData,
    pumpControllersData,
    borewellPumpControllersData,
    pumpsData,
    levelControllersData,
    tanksData,
    valvesData,
    sourcesData,
    flowmetersData,
  };

  /* ══════════════════════════════════════════
     RENDER
  ══════════════════════════════════════════ */
  return (
    <Layout>
      {/* ─── FLOW MODAL ─── */}
      {showFlowModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl h-[calc(100vh-2rem)] max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="flex shrink-0 items-center justify-between px-6 py-4 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white">
              <div>
                <h2 className="text-2xl font-bold text-slate-800">
                  {editingFlowId ? "Edit Flow" : "Add New Flow"}
                </h2>
                <p className="text-sm text-slate-500 mt-0.5">
                  {editingFlowId
                    ? "Update your water flow configuration"
                    : "Configure your water flow path"}
                </p>
              </div>
              <button
                onClick={closeModal}
                className="text-slate-400 hover:text-slate-700 transition-colors p-2 rounded-lg hover:bg-slate-200"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto bg-slate-50/80 p-4 lg:overflow-hidden">
              <div className="grid min-h-0 grid-cols-1 gap-4 lg:h-full lg:grid-cols-[minmax(0,1fr)_360px]">
                <div className="min-h-0 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm lg:h-full">
                  {/* Tabs Navigation */}
                  <Tabs
                    defaultValue="basic"
                    className="flex h-full min-h-0 flex-col overflow-hidden"
                  >
                    <TabsList className="w-full shrink-0 justify-start overflow-x-auto border-b border-slate-200 rounded-none bg-slate-50 px-5 py-0 gap-6">
                      <TabsTrigger
                        value="basic"
                        className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 data-[state=active]:bg-transparent px-0 py-3"
                      >
                        <Building2 className="h-4 w-4 mr-2" />
                        Basic Info
                      </TabsTrigger>
                      <TabsTrigger
                        value="atbpc"
                        className="rounded-none border-b-2 border-transparent data-[state=active]:border-emerald-500 data-[state=active]:bg-transparent px-0 py-3"
                      >
                        <Power className="h-4 w-4 mr-2" />
                        ATBPC Conf.
                      </TabsTrigger>
                      <TabsTrigger
                        value="pump"
                        className="rounded-none border-b-2 border-transparent data-[state=active]:border-yellow-500 data-[state=active]:bg-transparent px-0 py-3"
                      >
                        <Zap className="h-4 w-4 mr-2" />
                        Pump Config
                      </TabsTrigger>
                      <TabsTrigger
                        value="level"
                        className="rounded-none border-b-2 border-transparent data-[state=active]:border-indigo-500 data-[state=active]:bg-transparent px-0 py-3"
                      >
                        <Network className="h-4 w-4 mr-2" />
                        Level Control
                      </TabsTrigger>
                    </TabsList>

                    {/* Tab Content */}
                    <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
                      {/* TAB 1: BASIC INFO */}
                      <TabsContent
                        value="basic"
                        className="px-6 pt-6 pb-12 space-y-6 m-0"
                      >
                        {/* Mandatory Section */}
                        <div className="space-y-4">
                          <div className="flex items-center gap-2 pb-3 border-b border-slate-200">
                            <div className="w-1 h-6 bg-blue-500 rounded-full"></div>
                            <h3 className="font-semibold text-slate-800">
                              Basic Configuration
                            </h3>
                          </div>

                          {/* Tower */}
                          <div>
                            <Label className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-2">
                              <Layers className="h-4 w-4 text-purple-500" />
                              Select Tower
                            </Label>
                            <Select
                              value={
                                currentFlow.tower_id || OPTIONAL_TOWER_VALUE
                              }
                              onValueChange={(v) =>
                                setCurrentFlow({
                                  ...currentFlow,
                                  tower_id: v === OPTIONAL_TOWER_VALUE ? "" : v,
                                })
                              }
                            >
                              <SelectTrigger className="w-full h-10">
                                <SelectValue placeholder="Choose a tower" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value={OPTIONAL_TOWER_VALUE}>
                                  No tower
                                </SelectItem>
                                {availableTowers.map((t) => (
                                  <SelectItem key={t.id} value={t.id}>
                                    {t.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Assign Users */}
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <Label className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                                <Users className="h-4 w-4 text-slate-600" />
                                Assign Users{" "}
                                <span className="text-red-500">*</span>
                              </Label>
                              {getFlowUserIds(currentFlow).length > 0 && (
                                <button
                                  onClick={() =>
                                    setCurrentFlow({
                                      ...currentFlow,
                                      user_ids: [],
                                      user_id: "",
                                    })
                                  }
                                  className="text-xs text-slate-400 hover:text-slate-600 underline font-medium"
                                >
                                  Clear
                                </button>
                              )}
                            </div>
                            <div className="rounded-lg border border-slate-200 bg-white max-h-44 overflow-y-auto">
                              {adminsData.length > 0 ? (
                                adminsData.map((admin) => {
                                  const selected = getFlowUserIds(
                                    currentFlow,
                                  ).includes(admin.id);
                                  return (
                                    <button
                                      key={admin.id}
                                      type="button"
                                      onClick={() =>
                                        toggleAssignedUser(admin.id)
                                      }
                                      className={`w-full flex items-center justify-between gap-3 px-3 py-2 text-left text-sm transition-colors ${
                                        selected
                                          ? "bg-slate-100 text-slate-900"
                                          : "hover:bg-slate-50 text-slate-700"
                                      }`}
                                    >
                                      <span className="truncate">
                                        {admin.name || admin.email}
                                      </span>
                                      <span
                                        className={`h-4 w-4 rounded border flex items-center justify-center flex-shrink-0 ${
                                          selected
                                            ? "bg-slate-900 border-slate-900"
                                            : "border-slate-300"
                                        }`}
                                      >
                                        {selected && (
                                          <span className="h-1.5 w-1.5 rounded-full bg-white" />
                                        )}
                                      </span>
                                    </button>
                                  );
                                })
                              ) : (
                                <div className="px-3 py-3 text-sm text-slate-500">
                                  No admin users available
                                </div>
                              )}
                            </div>
                            {getFlowUserIds(currentFlow).length > 0 && (
                              <div className="flex flex-wrap gap-2 mt-2">
                                {getFlowUserIds(currentFlow).map((adminId) => (
                                  <span
                                    key={adminId}
                                    className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700"
                                  >
                                    {getAdminLabel(adminId)}
                                    <button
                                      type="button"
                                      onClick={() =>
                                        toggleAssignedUser(adminId)
                                      }
                                      className="text-slate-400 hover:text-red-600"
                                    >
                                      <X className="h-3 w-3" />
                                    </button>
                                  </span>
                                ))}
                              </div>
                            )}
                            <p className="text-xs text-slate-500 mt-1">
                              Assign one or more admin users to manage this
                              tower
                            </p>
                          </div>
                        </div>

                        {/* Optional UG Tank Section */}
                        <div className="space-y-4">
                          <div className="flex items-center gap-2 pb-3 border-b border-slate-200">
                            <div className="w-1 h-6 bg-cyan-500 rounded-full"></div>
                            <h3 className="font-semibold text-slate-800">
                              Source Tank
                            </h3>
                            <span className="ml-auto text-xs px-2 py-1 bg-cyan-50 text-cyan-700 rounded-full border border-cyan-200">
                              Optional
                            </span>
                          </div>

                          {/* UG Tank */}
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <Label className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                                <Droplets className="h-4 w-4 text-cyan-500" />
                                Select UG Tank
                              </Label>
                              {currentFlow.ug_tank_id && (
                                <button
                                  onClick={() =>
                                    setCurrentFlow({
                                      ...currentFlow,
                                      ug_tank_id: "",
                                    })
                                  }
                                  className="text-xs text-slate-400 hover:text-slate-600 underline font-medium"
                                >
                                  Clear
                                </button>
                              )}
                            </div>
                            <Select
                              value={currentFlow.ug_tank_id}
                              onValueChange={(v) =>
                                setCurrentFlow({
                                  ...currentFlow,
                                  ug_tank_id: v,
                                })
                              }
                            >
                              <SelectTrigger className="w-full h-10">
                                <SelectValue placeholder="Choose a UG tank (optional)" />
                              </SelectTrigger>
                              <SelectContent>
                                {ugTanksData.map((t) => (
                                  <SelectItem
                                    key={t.ug_tank_id}
                                    value={t.ug_tank_id}
                                  >
                                    {t.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <p className="text-xs text-slate-500 mt-1">
                              Specify the source underground tank for this flow
                              (optional)
                            </p>
                          </div>
                        </div>
                      </TabsContent>

                      {/* TAB 2: ATBPC CONFIG */}
                      <TabsContent
                        value="atbpc"
                        className="px-6 pt-6 pb-12 space-y-6 m-0"
                      >
                        <div className="space-y-5">
                          <div className="flex items-center gap-2 pb-3 border-b border-slate-200">
                            <div className="w-1 h-6 bg-emerald-500 rounded-full"></div>
                            <h3 className="font-semibold text-slate-800">
                              ATBPC Configuration
                            </h3>
                            <span className="ml-auto text-xs px-2 py-1 bg-emerald-50 text-emerald-700 rounded-full border border-emerald-200">
                              Optional
                            </span>
                          </div>

                          {borewellPumpControllersData.length > 0 ? (
                            <div>
                              <div className="flex items-center justify-between mb-2">
                                <Label className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                                  <Power className="h-4 w-4 text-emerald-500" />
                                  Select ATBPC
                                </Label>
                                {currentATBPCConfig.atbpc_device_id && (
                                  <button
                                    onClick={() =>
                                      updateATBPCConfig({
                                        atbpc_device_id: "",
                                        pump_configurations: [],
                                      })
                                    }
                                    className="text-xs text-slate-400 hover:text-slate-600 underline font-medium"
                                  >
                                    Clear
                                  </button>
                                )}
                              </div>
                              <Select
                                value={currentATBPCConfig.atbpc_device_id}
                                onValueChange={(value) =>
                                  updateATBPCConfig({ atbpc_device_id: value })
                                }
                              >
                                <SelectTrigger className="w-full h-10">
                                  <SelectValue placeholder="Choose a borewell pump controller" />
                                </SelectTrigger>
                                <SelectContent>
                                  {borewellPumpControllersData.map(
                                    (controller) => (
                                      <SelectItem
                                        key={controller.device_id}
                                        value={controller.device_id}
                                      >
                                        {controller.name ||
                                          controller.device_id}
                                      </SelectItem>
                                    ),
                                  )}
                                </SelectContent>
                              </Select>
                            </div>
                          ) : (
                            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-700">
                              No borewell pump controllers available. Create one
                              in the Devices tab first.
                            </div>
                          )}

                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <Label className="text-sm font-semibold text-slate-700">
                                Pump Paths
                              </Label>
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={addATBPCPumpConfiguration}
                                disabled={!currentATBPCConfig.atbpc_device_id}
                                className="flex items-center gap-1"
                              >
                                <Plus className="h-4 w-4" />
                                Add Path
                              </Button>
                            </div>

                            {currentATBPCConfig.pump_configurations.length ===
                            0 ? (
                              <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">
                                Add ATBPC paths to map Source, Pump, optional
                                Flowmeter, and Destination.
                              </div>
                            ) : (
                              <div className="space-y-4">
                                {currentATBPCConfig.pump_configurations.map(
                                  (mapping, index) => (
                                    <div
                                      key={index}
                                      className="rounded-lg border border-slate-200 bg-white p-4"
                                    >
                                      <div className="flex items-center justify-between mb-4">
                                        <div className="text-sm font-semibold text-slate-700">
                                          Path {index + 1}
                                        </div>
                                        <button
                                          type="button"
                                          onClick={() =>
                                            removeATBPCPumpConfiguration(index)
                                          }
                                          className="text-slate-300 hover:text-red-600 transition-colors p-1 rounded hover:bg-red-50"
                                          title="Remove path"
                                        >
                                          <X className="h-4 w-4" />
                                        </button>
                                      </div>

                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                          <Label className="text-xs text-slate-600 font-medium mb-2 flex items-center gap-1">
                                            <Activity className="h-3 w-3 text-orange-500" />
                                            Pump
                                          </Label>
                                          <Select
                                            value={mapping.pump_id}
                                            onValueChange={(value) =>
                                              updateATBPCPumpConfiguration(
                                                index,
                                                { pump_id: value },
                                              )
                                            }
                                          >
                                            <SelectTrigger className="h-10">
                                              <SelectValue placeholder="Select pump" />
                                            </SelectTrigger>
                                            <SelectContent>
                                              {pumpsData.map((pump) => (
                                                <SelectItem
                                                  key={pump.pump_id}
                                                  value={pump.pump_id}
                                                >
                                                  {pump.name || pump.pump_id}
                                                </SelectItem>
                                              ))}
                                            </SelectContent>
                                          </Select>
                                        </div>

                                        <div>
                                          <Label className="text-xs text-slate-600 font-medium mb-2 flex items-center gap-1">
                                            <Layers className="h-3 w-3 text-teal-500" />
                                            Destination
                                          </Label>
                                          <Select
                                            value={mapping.destination_tank_id}
                                            onValueChange={(value) =>
                                              updateATBPCPumpConfiguration(
                                                index,
                                                {
                                                  destination_tank_id: value,
                                                },
                                              )
                                            }
                                          >
                                            <SelectTrigger className="h-10">
                                              <SelectValue placeholder="Select destination tank" />
                                            </SelectTrigger>
                                            <SelectContent>
                                              {atbpcDestinationOptions.map(
                                                (option) => (
                                                  <SelectItem
                                                    key={option.key}
                                                    value={option.value}
                                                  >
                                                    {option.label} (
                                                    {option.group})
                                                  </SelectItem>
                                                ),
                                              )}
                                            </SelectContent>
                                          </Select>
                                        </div>

                                        <div>
                                          <Label className="text-xs text-slate-600 font-medium mb-2 flex items-center gap-1">
                                            <Droplets className="h-3 w-3 text-sky-500" />
                                            Source
                                          </Label>
                                          <Select
                                            value={mapping.source_id}
                                            onValueChange={(value) =>
                                              updateATBPCPumpConfiguration(
                                                index,
                                                { source_id: value },
                                              )
                                            }
                                          >
                                            <SelectTrigger className="h-10">
                                              <SelectValue placeholder="Select source" />
                                            </SelectTrigger>
                                            <SelectContent>
                                              {atbpcSourceOptions.map(
                                                (option) => (
                                                  <SelectItem
                                                    key={option.key}
                                                    value={option.value}
                                                  >
                                                    {option.label} (
                                                    {option.group})
                                                  </SelectItem>
                                                ),
                                              )}
                                            </SelectContent>
                                          </Select>
                                        </div>

                                        <div>
                                          <div className="mb-2 flex items-center justify-between gap-2">
                                            <Label className="flex items-center gap-1 text-xs font-medium text-slate-600">
                                              <Waves className="h-3 w-3 text-cyan-500" />
                                              Flowmeter
                                              <span className="font-normal text-slate-400">
                                                (Optional)
                                              </span>
                                            </Label>
                                            {mapping.flowmeter_id && (
                                              <button
                                                type="button"
                                                onClick={() =>
                                                  updateATBPCPumpConfiguration(
                                                    index,
                                                    { flowmeter_id: "" },
                                                  )
                                                }
                                                className="inline-flex h-5 w-5 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
                                                title="Clear flowmeter"
                                                aria-label="Clear flowmeter"
                                              >
                                                <X className="h-3 w-3" />
                                              </button>
                                            )}
                                          </div>
                                          <Select
                                            value={mapping.flowmeter_id}
                                            onValueChange={(value) =>
                                              updateATBPCPumpConfiguration(
                                                index,
                                                { flowmeter_id: value },
                                              )
                                            }
                                          >
                                            <SelectTrigger className="h-10">
                                              <SelectValue placeholder="Select flowmeter" />
                                            </SelectTrigger>
                                            <SelectContent>
                                              {flowmetersData.map(
                                                (flowmeterItem, itemIndex) => (
                                                  <SelectItem
                                                    key={flowmeterItem.id}
                                                    value={flowmeterItem.id}
                                                  >
                                                    {flowmeterItem.name ||
                                                      `Flowmeter ${itemIndex + 1} - Slave ${flowmeterItem.slave_id}`}
                                                  </SelectItem>
                                                ),
                                              )}
                                            </SelectContent>
                                          </Select>
                                        </div>
                                      </div>
                                    </div>
                                  ),
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </TabsContent>

                      {/* TAB 3: PUMP CONFIG */}
                      <TabsContent
                        value="pump"
                        className="px-6 pt-6 pb-12 space-y-6 m-0"
                      >
                        <div className="space-y-4">
                          <div className="flex items-center gap-2 pb-3 border-b border-slate-200">
                            <div className="w-1 h-6 bg-yellow-500 rounded-full"></div>
                            <h3 className="font-semibold text-slate-800">
                              Pump Configuration
                            </h3>
                            <span className="ml-auto text-xs px-2 py-1 bg-yellow-50 text-yellow-700 rounded-full border border-yellow-200">
                              Optional
                            </span>
                          </div>

                          {pumpControllersData.length > 0 ? (
                            <div>
                              <div className="flex items-center justify-between mb-2">
                                <Label className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                                  <Zap className="h-4 w-4 text-yellow-500" />
                                  Select Pump Controllers
                                </Label>
                                {currentPumpControllerIds.length > 0 && (
                                  <button
                                    onClick={() =>
                                      setCurrentFlow(
                                        normalizeFlowForForm(
                                          {
                                            ...currentFlow,
                                            pump_controller_id: "",
                                            pump_controller_ids: [],
                                            level_controller_mappings:
                                              currentLevelControllerMappings.map(
                                                (entry) => ({
                                                  ...entry,
                                                  valve_tank_mappings: (
                                                    entry.valve_tank_mappings ||
                                                    []
                                                  ).map((mapping) => ({
                                                    ...mapping,
                                                    pump_controller_id: "",
                                                  })),
                                                }),
                                              ),
                                          },
                                          societiesData?.id,
                                        ),
                                      )
                                    }
                                    className="text-xs text-slate-400 hover:text-slate-600 underline font-medium"
                                  >
                                    Clear
                                  </button>
                                )}
                              </div>
                              <div className="rounded-lg border border-slate-200 bg-white max-h-56 overflow-y-auto">
                                {pumpControllersData.map((pc) => {
                                  const selected =
                                    currentPumpControllerIds.includes(
                                      pc.device_id,
                                    );
                                  return (
                                    <button
                                      key={pc.device_id}
                                      type="button"
                                      onClick={() =>
                                        togglePumpController(pc.device_id)
                                      }
                                      className={`w-full flex items-center justify-between gap-3 px-3 py-2 text-left text-sm transition-colors ${
                                        selected
                                          ? "bg-yellow-50 text-slate-900"
                                          : "hover:bg-slate-50 text-slate-700"
                                      }`}
                                    >
                                      <span className="truncate">
                                        {pc.name || pc.device_id}
                                      </span>
                                      <span
                                        className={`h-4 w-4 rounded border flex items-center justify-center flex-shrink-0 ${
                                          selected
                                            ? "bg-yellow-500 border-yellow-500"
                                            : "border-slate-300"
                                        }`}
                                      >
                                        {selected && (
                                          <span className="h-1.5 w-1.5 rounded-full bg-white" />
                                        )}
                                      </span>
                                    </button>
                                  );
                                })}
                              </div>
                              {currentPumpControllerIds.length > 0 && (
                                <div className="flex flex-wrap gap-2 mt-2">
                                  {currentPumpControllerIds.map(
                                    (controllerId) => (
                                      <span
                                        key={controllerId}
                                        className="inline-flex items-center gap-1 rounded-full bg-yellow-50 px-2.5 py-1 text-xs font-medium text-yellow-800 border border-yellow-100"
                                      >
                                        {getAssetName(
                                          pumpControllersData,
                                          "device_id",
                                          controllerId,
                                        )}
                                        <button
                                          type="button"
                                          onClick={() =>
                                            togglePumpController(controllerId)
                                          }
                                          className="text-yellow-500 hover:text-red-600"
                                        >
                                          <X className="h-3 w-3" />
                                        </button>
                                      </span>
                                    ),
                                  )}
                                </div>
                              )}
                              <p className="text-xs text-slate-500 mt-1">
                                Controls the pump operation and water flow.
                                Specific pumps can be selected for individual
                                valve & tank mappings in the Level Control tab.
                              </p>
                            </div>
                          ) : (
                            <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-600">
                              No pump controllers available. Create some in the
                              Devices tab first.
                            </div>
                          )}
                        </div>
                      </TabsContent>

                      {/* TAB 4: LEVEL CONTROL */}
                      <TabsContent
                        value="level"
                        className="px-6 pt-6 pb-12 space-y-6 m-0"
                      >
                        <div className="space-y-4">
                          <div className="flex items-center gap-2 pb-3 border-b border-slate-200">
                            <div className="w-1 h-6 bg-indigo-500 rounded-full"></div>
                            <h3 className="font-semibold text-slate-800">
                              Level Control
                            </h3>
                            <span className="ml-auto text-xs px-2 py-1 bg-indigo-50 text-indigo-700 rounded-full border border-indigo-200">
                              Optional
                            </span>
                          </div>

                          {levelControllersData.length > 0 ? (
                            <>
                              <div>
                                <div className="flex items-center justify-between mb-2">
                                  <Label className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                                    <Network className="h-4 w-4 text-indigo-500" />
                                    Select Level Controllers
                                  </Label>
                                  {currentLevelControllerIds.length > 0 && (
                                    <button
                                      onClick={() =>
                                        setCurrentFlow(
                                          normalizeFlowForForm(
                                            {
                                              ...currentFlow,
                                              level_controller_id: "",
                                              level_controller_mappings: [],
                                              valve_tank_mappings: [],
                                            },
                                            societiesData?.id,
                                          ),
                                        )
                                      }
                                      className="text-xs text-slate-400 hover:text-slate-600 underline font-medium"
                                    >
                                      Clear
                                    </button>
                                  )}
                                </div>
                                <div className="rounded-lg border border-slate-200 bg-white max-h-56 overflow-y-auto">
                                  {levelControllersData.map((lc) => {
                                    const selected =
                                      currentLevelControllerIds.includes(
                                        lc.device_id,
                                      );
                                    const usedElsewhere =
                                      usedLevelControllerIds.includes(
                                        lc.device_id,
                                      );
                                    return (
                                      <button
                                        key={lc.device_id}
                                        type="button"
                                        disabled={usedElsewhere && !selected}
                                        onClick={() =>
                                          toggleLevelController(lc.device_id)
                                        }
                                        className={`w-full flex items-center justify-between gap-3 px-3 py-2 text-left text-sm transition-colors ${
                                          selected
                                            ? "bg-indigo-50 text-slate-900"
                                            : usedElsewhere
                                              ? "cursor-not-allowed bg-slate-50 text-slate-300"
                                              : "hover:bg-slate-50 text-slate-700"
                                        }`}
                                      >
                                        <span className="truncate">
                                          {lc.name || lc.device_id}
                                        </span>
                                        <span
                                          className={`h-4 w-4 rounded border flex items-center justify-center flex-shrink-0 ${
                                            selected
                                              ? "bg-indigo-500 border-indigo-500"
                                              : "border-slate-300"
                                          }`}
                                        >
                                          {selected && (
                                            <span className="h-1.5 w-1.5 rounded-full bg-white" />
                                          )}
                                        </span>
                                      </button>
                                    );
                                  })}
                                </div>
                                {currentLevelControllerIds.length > 0 && (
                                  <div className="flex flex-wrap gap-2 mt-2">
                                    {currentLevelControllerIds.map(
                                      (controllerId) => (
                                        <span
                                          key={controllerId}
                                          className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-medium text-indigo-800 border border-indigo-100"
                                        >
                                          {getAssetName(
                                            levelControllersData,
                                            "device_id",
                                            controllerId,
                                          )}
                                          <button
                                            type="button"
                                            onClick={() =>
                                              toggleLevelController(
                                                controllerId,
                                              )
                                            }
                                            className="text-indigo-500 hover:text-red-600"
                                          >
                                            <X className="h-3 w-3" />
                                          </button>
                                        </span>
                                      ),
                                    )}
                                  </div>
                                )}
                                <p className="text-xs text-slate-500 mt-1">
                                  Each selected controller gets its own valve
                                  and tank mappings
                                </p>
                              </div>

                              {currentLevelControllerMappings.map((entry) => {
                                const draft =
                                  currentVTMapping.level_controller_id ===
                                  entry.level_controller_id
                                    ? currentVTMapping
                                    : getEmptyVTMapping(
                                        entry.level_controller_id,
                                      );
                                const gridCols =
                                  currentPumpControllerIds.length > 0
                                    ? "md:grid-cols-4"
                                    : "md:grid-cols-2";

                                return (
                                  <div
                                    key={entry.level_controller_id}
                                    className="pt-4 border-t border-slate-100"
                                  >
                                    <Label className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                                      <Network className="h-4 w-4 text-indigo-500" />
                                      {getAssetName(
                                        levelControllersData,
                                        "device_id",
                                        entry.level_controller_id,
                                      )}
                                    </Label>
                                    <p className="text-xs text-slate-500 mb-4">
                                      Add valve and tank access for this level
                                      controller.
                                    </p>

                                    <div
                                      className={`${gridCols} grid grid-cols-1 gap-4 mb-4 p-4 bg-slate-50 rounded-lg border border-slate-200`}
                                    >
                                      <div>
                                        <Label className="text-xs text-slate-600 font-medium mb-2 flex items-center gap-1">
                                          <FlaskConical className="h-3 w-3 text-pink-500" />
                                          Valve (Optional)
                                        </Label>
                                        <Select
                                          value={draft.valve_id}
                                          onValueChange={(v) =>
                                            updateCurrentMapping(
                                              entry.level_controller_id,
                                              {
                                                valve_id: v,
                                              },
                                            )
                                          }
                                        >
                                          <SelectTrigger className="h-10">
                                            <SelectValue placeholder="Select valve" />
                                          </SelectTrigger>
                                          <SelectContent>
                                            {availableValves.map((v) => (
                                              <SelectItem
                                                key={v.valve_id}
                                                value={v.valve_id}
                                              >
                                                {getAssetName(
                                                  valvesData,
                                                  "valve_id",
                                                  v.valve_id,
                                                )}
                                              </SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                      </div>
                                      <div>
                                        <Label className="text-xs text-slate-600 font-medium mb-2 flex items-center gap-1">
                                          <Waves className="h-3 w-3 text-teal-500" />
                                          Tank (Optional)
                                        </Label>
                                        <Select
                                          value={draft.tank_id}
                                          onValueChange={(v) =>
                                            updateCurrentMapping(
                                              entry.level_controller_id,
                                              {
                                                tank_id: v,
                                              },
                                            )
                                          }
                                        >
                                          <SelectTrigger className="h-10">
                                            <SelectValue placeholder="Select tank" />
                                          </SelectTrigger>
                                          <SelectContent>
                                            {availableTanks.map((t) => (
                                              <SelectItem
                                                key={t.tank_id}
                                                value={t.tank_id}
                                              >
                                                {getAssetName(
                                                  tanksData,
                                                  "tank_id",
                                                  t.tank_id,
                                                )}
                                              </SelectItem>
                                            ))}
                                            {/* Tanks already mapped on this level controller can
                                                be picked again to connect another pump. */}
                                            {uniqueFlowIds(
                                              (entry.valve_tank_mappings || []).map(
                                                (mapping) => mapping.tank_id,
                                              ),
                                            )
                                              .filter(
                                                (tankId) =>
                                                  !availableTanks.some(
                                                    (t) => t.tank_id === tankId,
                                                  ),
                                              )
                                              .map((tankId) => (
                                                <SelectItem
                                                  key={`mapped-${tankId}`}
                                                  value={tankId}
                                                >
                                                  {getAssetName(
                                                    tanksData,
                                                    "tank_id",
                                                    tankId,
                                                  )}{" "}
                                                  (mapped — add another pump)
                                                </SelectItem>
                                              ))}
                                          </SelectContent>
                                        </Select>
                                      </div>
                                      {currentPumpControllerIds.length > 0 && (
                                        <>
                                          <div>
                                            <Label className="text-xs text-slate-600 font-medium mb-2 flex items-center gap-1">
                                              <Zap className="h-3 w-3 text-yellow-500" />
                                              Pump Ctrl (Optional)
                                            </Label>
                                            <Select
                                              value={draft.pump_controller_id}
                                              onValueChange={(v) =>
                                                updateCurrentMapping(
                                                  entry.level_controller_id,
                                                  { pump_controller_id: v },
                                                )
                                              }
                                            >
                                              <SelectTrigger className="h-10">
                                                <SelectValue placeholder="Select controller" />
                                              </SelectTrigger>
                                              <SelectContent>
                                                {currentPumpControllerIds.map(
                                                  (controllerId) => (
                                                    <SelectItem
                                                      key={controllerId}
                                                      value={controllerId}
                                                    >
                                                      {getAssetName(
                                                        pumpControllersData,
                                                        "device_id",
                                                        controllerId,
                                                      )}
                                                    </SelectItem>
                                                  ),
                                                )}
                                              </SelectContent>
                                            </Select>
                                          </div>
                                          <div>
                                            <Label className="text-xs text-slate-600 font-medium mb-2 flex items-center gap-1">
                                              <Activity className="h-3 w-3 text-orange-500" />
                                              Pump (Optional)
                                            </Label>
                                            <Select
                                              value={draft.pump_id}
                                              onValueChange={(v) =>
                                                updateCurrentMapping(
                                                  entry.level_controller_id,
                                                  { pump_id: v },
                                                )
                                              }
                                            >
                                              <SelectTrigger className="h-10">
                                                <SelectValue placeholder="Select pump" />
                                              </SelectTrigger>
                                              <SelectContent>
                                                {pumpsData.map((p) => (
                                                  <SelectItem
                                                    key={p.pump_id}
                                                    value={p.pump_id}
                                                  >
                                                    {getAssetName(
                                                      pumpsData,
                                                      "pump_id",
                                                      p.pump_id,
                                                    )}
                                                  </SelectItem>
                                                ))}
                                              </SelectContent>
                                            </Select>
                                          </div>
                                        </>
                                      )}
                                    </div>

                                    <Button
                                      size="sm"
                                      onClick={() =>
                                        addVTMapping(entry.level_controller_id)
                                      }
                                      disabled={
                                        !draft.valve_id && !draft.tank_id
                                      }
                                      className="w-full flex items-center gap-1 bg-slate-900 hover:bg-slate-700 text-white"
                                    >
                                      <Plus className="h-4 w-4" /> Add Mapping
                                    </Button>
                                    {currentPumpControllerIds.length > 0 && (
                                      <p className="mt-2 text-xs text-slate-500">
                                        To connect more than one pump to a tank,
                                        select the same tank again with another
                                        pump and click Add Mapping.
                                      </p>
                                    )}

                                    {(entry.valve_tank_mappings || []).length >
                                      0 && (
                                      <div className="mt-4 space-y-2">
                                        <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
                                          Added Mappings (
                                          {entry.valve_tank_mappings.length})
                                        </p>
                                        <div className="space-y-2 max-h-40 overflow-y-auto">
                                          {entry.valve_tank_mappings.map(
                                            (m, i) => (
                                              <div
                                                key={`${entry.level_controller_id}-${i}`}
                                                className="flex items-center justify-between bg-white rounded-lg border border-slate-200 px-3 py-2 text-sm hover:border-slate-300 transition-colors"
                                              >
                                                <div className="flex items-center gap-3 flex-wrap">
                                                  {getMappingPumps(m).length > 0 && (
                                                    <>
                                                      {/* Every pump feeding this tank */}
                                                      <div className="flex flex-col gap-1">
                                                        {getMappingPumps(m).map(
                                                          (pump) => (
                                                            <span
                                                              key={pump.pump_id}
                                                              className="inline-flex items-center gap-1.5 rounded-md border border-orange-200 bg-orange-50 px-2 py-0.5 text-xs"
                                                            >
                                                              <Activity className="h-3 w-3 text-orange-600" />
                                                              <span className="font-medium text-orange-700">
                                                                {getAssetName(
                                                                  pumpsData,
                                                                  "pump_id",
                                                                  pump.pump_id,
                                                                )}
                                                              </span>
                                                              {pump.pump_controller_id && (
                                                                <span className="flex items-center gap-0.5 text-yellow-700">
                                                                  <Zap className="h-3 w-3" />
                                                                  {getAssetName(
                                                                    pumpControllersData,
                                                                    "device_id",
                                                                    pump.pump_controller_id,
                                                                  )}
                                                                </span>
                                                              )}
                                                              {getMappingPumps(m).length > 1 && (
                                                                <button
                                                                  type="button"
                                                                  title="Remove this pump from the tank"
                                                                  onClick={() =>
                                                                    removeMappingPump(
                                                                      entry.level_controller_id,
                                                                      i,
                                                                      pump.pump_id,
                                                                    )
                                                                  }
                                                                  className="text-orange-300 hover:text-red-600"
                                                                >
                                                                  <X className="h-3 w-3" />
                                                                </button>
                                                              )}
                                                            </span>
                                                          ),
                                                        )}
                                                      </div>
                                                      {(m.valve_id ||
                                                        m.tank_id) && (
                                                        <ChevronRight className="h-3 w-3 text-slate-300" />
                                                      )}
                                                    </>
                                                  )}
                                                  {!getMappingPumps(m).length &&
                                                    m.pump_controller_id && (
                                                    <>
                                                      <span className="text-yellow-700 font-medium flex items-center gap-1">
                                                        <Zap className="h-3 w-3" />
                                                        {getAssetName(
                                                          pumpControllersData,
                                                          "device_id",
                                                          m.pump_controller_id,
                                                        )}
                                                      </span>
                                                      {(m.valve_id || m.tank_id) && (
                                                        <ChevronRight className="h-3 w-3 text-slate-300" />
                                                      )}
                                                    </>
                                                  )}
                                                  {m.valve_id && (
                                                    <>
                                                      <span className="text-pink-600 font-medium flex items-center gap-1">
                                                        <FlaskConical className="h-3 w-3" />
                                                        {getAssetName(
                                                          valvesData,
                                                          "valve_id",
                                                          m.valve_id,
                                                        )}
                                                      </span>
                                                      {m.tank_id && (
                                                        <ChevronRight className="h-3 w-3 text-slate-300" />
                                                      )}
                                                    </>
                                                  )}
                                                  {m.tank_id && (
                                                    <span className="text-teal-600 font-medium flex items-center gap-1">
                                                      <Waves className="h-3 w-3" />
                                                      {getAssetName(
                                                        tanksData,
                                                        "tank_id",
                                                        m.tank_id,
                                                      )}
                                                    </span>
                                                  )}
                                                </div>
                                                <button
                                                  onClick={() =>
                                                    removeVTMapping(
                                                      entry.level_controller_id,
                                                      i,
                                                    )
                                                  }
                                                  className="text-slate-300 hover:text-red-600 transition-colors p-1 rounded hover:bg-red-50 flex-shrink-0"
                                                >
                                                  <X className="h-4 w-4" />
                                                </button>
                                              </div>
                                            ),
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </>
                          ) : (
                            <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg text-sm text-indigo-700">
                              No level controllers available. Create some in the
                              Devices tab first.
                            </div>
                          )}
                        </div>
                      </TabsContent>
                    </div>
                  </Tabs>
                </div>

                <LivePreviewTree
                  flow={currentFlow}
                  currentVTMapping={currentVTMapping}
                  {...treeProps}
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex shrink-0 justify-end gap-3 px-6 py-4 border-t border-slate-100 bg-white rounded-b-2xl">
              <Button
                variant="outline"
                onClick={closeModal}
                className="min-w-20"
              >
                Cancel
              </Button>
              <Button
                onClick={saveFlow}
                className="min-w-28 bg-slate-900 hover:bg-slate-700 text-white font-medium"
              >
                {editingFlowId ? "Update Flow" : "Save Flow"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ─── PAGE ─── */}
      <div className="space-y-6">
        <div>
          <h1 className="text-4xl font-bold text-slate-900">Setup Wizard</h1>
          <p className="text-slate-600 mt-1">
            Register society infrastructure and devices
          </p>
        </div>

        <Tabs defaultValue="society">
          <TabsList className="grid grid-cols-4 w-full">
            <TabsTrigger value="society">
              <Building2 className="h-4 w-4 mr-1" />
              Society Details
            </TabsTrigger>
            <TabsTrigger value="infra">
              <Droplets className="h-4 w-4 mr-1" />
              Infrastructure
            </TabsTrigger>
            <TabsTrigger value="devices">
              <Cpu className="h-4 w-4 mr-1" />
              Devices
            </TabsTrigger>
            <TabsTrigger value="architecture">
              <GitBranch className="h-4 w-4 mr-1" />
              Architecture
            </TabsTrigger>
          </TabsList>

          {/* ═══ SOCIETY ═══ */}
          <TabsContent value="society">
            <Card>
              <CardHeader>
                <CardTitle>Society Details</CardTitle>
                <CardDescription>
                  {societiesData?.id
                    ? "View or update society information"
                    : "Register society information"}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {(!societiesData?.id || editingSociety) && (
                  <form
                    onSubmit={submitSociety}
                    className="grid md:grid-cols-2 gap-4"
                  >
                    <div>
                      <Label>Society Name *</Label>
                      <Input
                        required
                        value={society.name}
                        onChange={(e) =>
                          setSociety({ ...society, name: e.target.value })
                        }
                      />
                    </div>
                    <div>
                      <Label>Location</Label>
                      <Input
                        value={society.location}
                        onChange={(e) =>
                          setSociety({ ...society, location: e.target.value })
                        }
                      />
                    </div>
                    <div className="md:col-span-2 flex gap-2">
                      <Button disabled={loading}>
                        {editingSociety ? "Update Society" : "Submit"}
                      </Button>
                      {editingSociety && (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={cancelEditSociety}
                          disabled={loading}
                        >
                          Cancel
                        </Button>
                      )}
                    </div>
                  </form>
                )}

                {societiesData?.id && !editingSociety && (
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                          Current Society
                        </p>
                        <h3 className="mt-1 text-lg font-semibold text-slate-900">
                          {societiesData.name || "-"}
                        </h3>
                        <p className="text-sm text-slate-600">
                          {societiesData.location || "No location added"}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={startEditSociety}
                          disabled={loading}
                        >
                          Edit
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={deleteSociety}
                          disabled={loading}
                          className="text-red-600 hover:bg-red-50 hover:text-red-700"
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ═══ INFRA ═══ */}
          <TabsContent value="infra" className="space-y-6">
            {/* Source */}
            <Card>
              <CardHeader>
                <CardTitle>Add Source</CardTitle>
                <CardDescription>
                  Create borewell or tank sources for this society. View, edit,
                  or delete them from Components.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-3 md:grid-cols-4">
                  <Input
                    placeholder="Source ID"
                    value={source.source_id}
                    onChange={(event) =>
                      setSource((prev) => ({
                        ...prev,
                        source_id: event.target.value,
                      }))
                    }
                  />
                  <Input
                    placeholder="Source Name"
                    value={source.name}
                    onChange={(event) =>
                      setSource((prev) => ({
                        ...prev,
                        name: event.target.value,
                      }))
                    }
                  />
                  <Select
                    value={source.type}
                    onValueChange={(value) =>
                      setSource((prev) => ({ ...prev, type: value }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Source Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">Borewell</SelectItem>
                      <SelectItem value="1">Tank</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select
                    value={source.status}
                    onValueChange={(value) =>
                      setSource((prev) => ({ ...prev, status: value }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      {SOURCE_STATUS_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={source.sensor_type}
                    onValueChange={(value) =>
                      setSource((prev) => ({ ...prev, sensor_type: value }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sensor Type" />
                    </SelectTrigger>
                    <SelectContent>
                      {SOURCE_SENSOR_TYPE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    min={0}
                    max={500}
                    placeholder="Sensor Slave ID"
                    value={source.sensor_slave_id}
                    onChange={(event) =>
                      setSource((prev) => ({
                        ...prev,
                        sensor_slave_id: event.target.value,
                      }))
                    }
                  />
                  <Input
                    type="number"
                    min={0}
                    step="0.01"
                    placeholder="Cable Length (m)"
                    value={source.cable_length_m}
                    onChange={(event) =>
                      setSource((prev) => ({
                        ...prev,
                        cable_length_m: event.target.value,
                      }))
                    }
                  />
                  <Input
                    type="number"
                    min={0}
                    step="0.01"
                    placeholder="Tank Height"
                    value={source.tank_height}
                    onChange={(event) =>
                      setSource((prev) => ({
                        ...prev,
                        tank_height: event.target.value,
                      }))
                    }
                  />
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    placeholder="Low"
                    value={source.low}
                    onChange={(event) =>
                      setSource((prev) => ({
                        ...prev,
                        low: event.target.value,
                      }))
                    }
                  />
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    placeholder="Mid"
                    value={source.med}
                    onChange={(event) =>
                      setSource((prev) => ({
                        ...prev,
                        med: event.target.value,
                      }))
                    }
                  />
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    placeholder="High"
                    value={source.high}
                    onChange={(event) =>
                      setSource((prev) => ({
                        ...prev,
                        high: event.target.value,
                      }))
                    }
                  />
                  <Input
                    placeholder="Location"
                    value={source.location}
                    onChange={(event) =>
                      setSource((prev) => ({
                        ...prev,
                        location: event.target.value,
                      }))
                    }
                  />
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={Boolean(source.publish)}
                      onCheckedChange={(value) =>
                        setSource((prev) => ({ ...prev, publish: value }))
                      }
                    />
                    <span className="text-sm">Publish</span>
                  </div>
                  <Button onClick={submitSource} disabled={loading}>
                    Submit Source
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Flowmeter */}
            <Card>
              <CardHeader>
                <CardTitle>Add Flowmeter</CardTitle>
                <CardDescription>
                  Configure flowmeter registers for this society.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-slate-700">
                    Flowmeter
                  </h3>
                  <div className="grid gap-3 md:grid-cols-3">
                    <Input
                      required
                      placeholder="Flowmeter Name"
                      value={flowmeter.name}
                      onChange={(event) =>
                        setFlowmeter((prev) => ({
                          ...prev,
                          name: event.target.value,
                        }))
                      }
                    />
                    <Select
                      value={flowmeter.status}
                      onValueChange={(value) =>
                        setFlowmeter((prev) => ({
                          ...prev,
                          status: value,
                        }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Flowmeter Status" />
                      </SelectTrigger>
                      <SelectContent>
                        {CONNECTED_STATUS_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      required
                      type="number"
                      min={0}
                      max={500}
                      placeholder="Slave ID"
                      value={flowmeter.slave_id}
                      onChange={(event) =>
                        setFlowmeter((prev) => ({
                          ...prev,
                          slave_id: event.target.value,
                        }))
                      }
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-slate-700">
                    Totalizer
                  </h3>
                  <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-6">
                    {[
                      ["addr", "Address", "1"],
                      ["quantity", "Quantity", "1"],
                      ["data_type", "Data Type", "1"],
                      ["order", "Order", "1"],
                      ["scale", "Scale", "0.01"],
                      ["offset", "Offset", "0.01"],
                    ].map(([key, label, step]) =>
                      key === "data_type" || key === "order" ? (
                        <Select
                          key={key}
                          value={flowmeter.totalizer[key]}
                          onValueChange={(value) =>
                            setFlowmeter((prev) => ({
                              ...prev,
                              totalizer: {
                                ...prev.totalizer,
                                [key]: value,
                              },
                            }))
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={label} />
                          </SelectTrigger>
                          <SelectContent>
                            {(key === "data_type"
                              ? MODBUS_DATA_TYPE_OPTIONS
                              : MODBUS_ORDER_OPTIONS
                            ).map((option) => (
                              <SelectItem
                                key={option.value}
                                value={option.value}
                              >
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          key={key}
                          required
                          type="number"
                          min={
                            ["scale", "offset"].includes(key) ? undefined : 0
                          }
                          step={step}
                          placeholder={label}
                          value={flowmeter.totalizer[key]}
                          onChange={(event) =>
                            setFlowmeter((prev) => ({
                              ...prev,
                              totalizer: {
                                ...prev.totalizer,
                                [key]: event.target.value,
                              },
                            }))
                          }
                        />
                      ),
                    )}
                  </div>
                </div>

                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-slate-700">
                    Flowrate
                  </h3>
                  <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-6">
                    {[
                      ["addr", "Address", "1"],
                      ["quantity", "Quantity", "1"],
                      ["data_type", "Data Type", "1"],
                      ["order", "Order", "1"],
                      ["scale", "Scale", "0.001"],
                      ["offset", "Offset", "0.01"],
                    ].map(([key, label, step]) =>
                      key === "data_type" || key === "order" ? (
                        <Select
                          key={key}
                          value={flowmeter.flowrate[key]}
                          onValueChange={(value) =>
                            setFlowmeter((prev) => ({
                              ...prev,
                              flowrate: {
                                ...prev.flowrate,
                                [key]: value,
                              },
                            }))
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={label} />
                          </SelectTrigger>
                          <SelectContent>
                            {(key === "data_type"
                              ? MODBUS_DATA_TYPE_OPTIONS
                              : MODBUS_ORDER_OPTIONS
                            ).map((option) => (
                              <SelectItem
                                key={option.value}
                                value={option.value}
                              >
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          key={key}
                          required
                          type="number"
                          min={
                            ["scale", "offset"].includes(key) ? undefined : 0
                          }
                          step={step}
                          placeholder={label}
                          value={flowmeter.flowrate[key]}
                          onChange={(event) =>
                            setFlowmeter((prev) => ({
                              ...prev,
                              flowrate: {
                                ...prev.flowrate,
                                [key]: event.target.value,
                              },
                            }))
                          }
                        />
                      ),
                    )}
                  </div>
                </div>

                <Button onClick={submitFlowmeter} disabled={loading}>
                  Submit Flowmeter
                </Button>
              </CardContent>
            </Card>

            {/* UG Tanks */}
            <Card>
              <CardHeader>
                <CardTitle>Add Bulk UG Tanks</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    type="number"
                    max={20}
                    placeholder="Count"
                    value={ugCount}
                    onChange={(e) => setUgCount(e.target.value)}
                  />
                  <Button
                    onClick={() =>
                      setUgTanks(
                        generateRows(ugCount, {
                          ug_tank_id: "",
                          name: "",
                          capacity: "",
                          height: "",
                          low: "",
                          med: "",
                          high: "",
                          sensor_type: "",
                          sensor_slave_id: "",
                          control: true,
                          bme_offset: "",
                          publish: true,
                        }),
                      )
                    }
                  >
                    Generate
                  </Button>
                </div>
                {ugTanks.map((tank, i) => (
                  <div
                    key={i}
                    className="grid md:grid-cols-6 xl:grid-cols-12 gap-3"
                  >
                    <Input
                      required
                      placeholder="UG Tank ID"
                      value={tank.ug_tank_id}
                      onChange={(e) => {
                        const arr = [...ugTanks];
                        arr[i].ug_tank_id = e.target.value;
                        setUgTanks(arr);
                      }}
                    />
                    <Input
                      required
                      placeholder="Name"
                      value={tank.name}
                      onChange={(e) => {
                        const arr = [...ugTanks];
                        arr[i].name = e.target.value;
                        setUgTanks(arr);
                      }}
                    />
                    <Input
                      required
                      type="number"
                      placeholder="Capacity"
                      value={tank.capacity}
                      onChange={(e) => {
                        const arr = [...ugTanks];
                        arr[i].capacity = e.target.value;
                        setUgTanks(arr);
                      }}
                    />
                    <Input
                      required
                      type="number"
                      placeholder="Height"
                      value={tank.height}
                      onChange={(e) => {
                        const arr = [...ugTanks];
                        arr[i].height = e.target.value;
                        setUgTanks(arr);
                      }}
                    />
                    <Input
                      required
                      type="number"
                      min={0}
                      max={100}
                      placeholder="Low"
                      value={tank.low}
                      onChange={(e) => {
                        const arr = [...ugTanks];
                        arr[i].low = e.target.value;
                        setUgTanks(arr);
                      }}
                    />
                    <Input
                      required
                      type="number"
                      min={0}
                      max={100}
                      placeholder="Mid"
                      value={tank.med}
                      onChange={(e) => {
                        const arr = [...ugTanks];
                        arr[i].med = e.target.value;
                        setUgTanks(arr);
                      }}
                    />
                    <Input
                      required
                      type="number"
                      min={0}
                      max={100}
                      placeholder="High"
                      value={tank.high}
                      onChange={(e) => {
                        const arr = [...ugTanks];
                        arr[i].high = e.target.value;
                        setUgTanks(arr);
                      }}
                    />
                    <Select
                      value={tank.sensor_type || ""}
                      onValueChange={(val) => {
                        const arr = [...ugTanks];
                        arr[i].sensor_type = val;
                        setUgTanks(arr);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sensor Type" />
                      </SelectTrigger>
                      <SelectContent>
                        {TANK_SENSOR_TYPE_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      required
                      type="number"
                      min={1}
                      max={500}
                      placeholder="Sensor Slave ID"
                      value={tank.sensor_slave_id}
                      onChange={(e) => {
                        const arr = [...ugTanks];
                        arr[i].sensor_slave_id = e.target.value;
                        setUgTanks(arr);
                      }}
                    />
                    <Input
                      required
                      type="number"
                      step="0.01"
                      placeholder="BME Offset"
                      value={tank.bme_offset}
                      onChange={(e) => {
                        const arr = [...ugTanks];
                        arr[i].bme_offset = e.target.value;
                        setUgTanks(arr);
                      }}
                    />
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={Boolean(tank.publish)}
                        onCheckedChange={(val) => {
                          const arr = [...ugTanks];
                          arr[i].publish = val;
                          setUgTanks(arr);
                        }}
                      />
                      <span className="text-sm">Publish</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={Boolean(tank.control)}
                        onCheckedChange={(val) => {
                          const arr = [...ugTanks];
                          arr[i].control = val;
                          setUgTanks(arr);
                        }}
                      />
                      <span className="text-sm">Control</span>
                    </div>
                  </div>
                ))}
                {ugTanks.length > 0 && (
                  <Button onClick={() => submitBulkUGTanks(ugTanks)}>
                    Submit UG Tanks
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Towers */}
            <Card>
              <CardHeader>
                <CardTitle>Add Bulk Towers</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    type="number"
                    max={20}
                    placeholder="Count"
                    value={towerCount}
                    onChange={(e) => setTowerCount(e.target.value)}
                  />
                  <Button
                    onClick={() =>
                      setTowers(
                        generateRows(towerCount, { name: "", location: "" }),
                      )
                    }
                  >
                    Generate
                  </Button>
                </div>
                {towers.map((tower, i) => (
                  <div key={i} className="grid md:grid-cols-2 gap-3">
                    <Input
                      required
                      placeholder="Tower Name"
                      value={tower.name}
                      onChange={(e) => {
                        const arr = [...towers];
                        arr[i].name = e.target.value;
                        setTowers(arr);
                      }}
                    />
                    <Input
                      required
                      placeholder="Location"
                      value={tower.location}
                      onChange={(e) => {
                        const arr = [...towers];
                        arr[i].location = e.target.value;
                        setTowers(arr);
                      }}
                    />
                  </div>
                ))}
                {towers.length > 0 && (
                  <Button onClick={() => submitBulkTowers(towers)}>
                    Submit Towers
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Tanks */}
            <Card>
              <CardHeader>
                <CardTitle>Add Bulk Tanks</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    type="number"
                    max={MAX_TANK_ROWS}
                    placeholder="Count"
                    value={tankCount}
                    onChange={(e) => setTankCount(e.target.value)}
                  />
                  <Button
                    onClick={() =>
                      setTanks(
                        generateRows(tankCount, {
                          tank_id: "",
                          name: "",
                          capacity: "",
                          low: "",
                          med: "",
                          high: "",
                          sensor_type: "",
                          sensor_slave_id: "",
                          height: "",
                          control: false,
                          bme_offset: "",
                          publish: true,
                          cluster_count: "",
                          cluster_slave_id_start: "",
                        }, MAX_TANK_ROWS),
                      )
                    }
                  >
                    Generate
                  </Button>
                </div>
                {tanks.map((tank, i) => (
                  <div key={i} className="grid md:grid-cols-6 gap-3">
                    <Input
                      required
                      placeholder="Tank Id"
                      value={tank.tank_id}
                      onChange={(e) => {
                        const arr = [...tanks];
                        arr[i].tank_id = e.target.value;
                        setTanks(arr);
                      }}
                    />
                    <Input
                      required
                      placeholder="Name"
                      value={tank.name}
                      onChange={(e) => {
                        const arr = [...tanks];
                        arr[i].name = e.target.value;
                        setTanks(arr);
                      }}
                    />
                    <Input
                      required
                      type="number"
                      placeholder="Capacity"
                      value={tank.capacity}
                      onChange={(e) => {
                        const arr = [...tanks];
                        arr[i].capacity = e.target.value;
                        setTanks(arr);
                      }}
                    />
                    <Input
                      required
                      type="number"
                      min={0}
                      max={100}
                      placeholder="Low"
                      value={tank.low}
                      onChange={(e) => {
                        const arr = [...tanks];
                        arr[i].low = e.target.value;
                        setTanks(arr);
                      }}
                    />
                    <Input
                      required
                      type="number"
                      min={0}
                      max={100}
                      placeholder="Mid"
                      value={tank.med}
                      onChange={(e) => {
                        const arr = [...tanks];
                        arr[i].med = e.target.value;
                        setTanks(arr);
                      }}
                    />
                    <Input
                      required
                      type="number"
                      min={0}
                      max={100}
                      placeholder="High"
                      value={tank.high}
                      onChange={(e) => {
                        const arr = [...tanks];
                        arr[i].high = e.target.value;
                        setTanks(arr);
                      }}
                    />
                    <Select
                      value={tank.sensor_type || ""}
                      onValueChange={(val) => {
                        const arr = [...tanks];
                        arr[i].sensor_type = val;
                        setTanks(arr);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sensor Type" />
                      </SelectTrigger>
                      <SelectContent>
                        {TANK_SENSOR_TYPE_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      type="number"
                      min={1}
                      max={500}
                      placeholder="Sensor Slave ID"
                      value={tank.sensor_slave_id}
                      onChange={(e) => {
                        const arr = [...tanks];
                        arr[i].sensor_slave_id = e.target.value;
                        setTanks(arr);
                      }}
                    />
                    <Input
                      placeholder="Height"
                      type="number"
                      value={tank.height}
                      onChange={(e) => {
                        const arr = [...tanks];
                        arr[i].height = e.target.value;
                        setTanks(arr);
                      }}
                    />
                    <Input
                      required
                      type="number"
                      step="0.01"
                      placeholder="BME Offset"
                      value={tank.bme_offset}
                      onChange={(e) => {
                        const arr = [...tanks];
                        arr[i].bme_offset = e.target.value;
                        setTanks(arr);
                      }}
                    />
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={Boolean(tank.publish)}
                        onCheckedChange={(val) => {
                          const arr = [...tanks];
                          arr[i].publish = val;
                          setTanks(arr);
                        }}
                      />
                      <span className="text-sm">Publish</span>
                    </div>
                    <Input
                      required
                      type="number"
                      min={0}
                      placeholder="Cluster Count"
                      value={tank.cluster_count}
                      onChange={(e) => {
                        const arr = [...tanks];
                        arr[i].cluster_count = e.target.value;
                        setTanks(arr);
                      }}
                    />
                    <Input
                      required
                      type="number"
                      min={0}
                      max={500}
                      placeholder="Cluster Slave ID Start"
                      value={tank.cluster_slave_id_start}
                      onChange={(e) => {
                        const arr = [...tanks];
                        arr[i].cluster_slave_id_start = e.target.value;
                        setTanks(arr);
                      }}
                    />
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={tank.control}
                        onCheckedChange={(val) => {
                          const arr = [...tanks];
                          arr[i].control = val;
                          setTanks(arr);
                        }}
                      />
                      <span className="text-sm">Control</span>
                    </div>
                  </div>
                ))}
                {tanks.length > 0 && (
                  <Button onClick={() => submitBulkTanks(tanks)}>
                    Submit Tanks
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Additional Sensors */}
            <Card>
              <CardHeader>
                <CardTitle>Add Bulk Additional Sensors</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    type="number"
                    max={20}
                    placeholder="Count"
                    value={additionalSensorCount}
                    onChange={(e) => setAdditionalSensorCount(e.target.value)}
                  />
                  <Button
                    onClick={() =>
                      setAdditionalSensors(
                        generateRows(additionalSensorCount, {
                          s_id: "",
                          sensor_name: "",
                          unit: "",
                          device_id: "",
                          slave_id: "",
                          start_address: "",
                          quantity: "",
                          data_type: "",
                          order: "",
                          scale: "",
                          offset: "",
                        }),
                      )
                    }
                  >
                    Generate
                  </Button>
                </div>
                {additionalSensors.map((sensor, i) => (
                  <div
                    key={i}
                    className="grid md:grid-cols-6 xl:grid-cols-11 gap-3"
                  >
                    <Input
                      required
                      placeholder="Sensor ID"
                      value={sensor.s_id}
                      onChange={(e) => {
                        const arr = [...additionalSensors];
                        arr[i].s_id = e.target.value;
                        setAdditionalSensors(arr);
                      }}
                    />
                    <Input
                      required
                      placeholder="Sensor Name"
                      value={sensor.sensor_name}
                      onChange={(e) => {
                        const arr = [...additionalSensors];
                        arr[i].sensor_name = e.target.value;
                        setAdditionalSensors(arr);
                      }}
                    />
                    <Input
                      placeholder="Unit (e.g. bar, °C)"
                      value={sensor.unit || ""}
                      onChange={(e) => {
                        const arr = [...additionalSensors];
                        arr[i].unit = e.target.value;
                        setAdditionalSensors(arr);
                      }}
                    />
                    <Select
                      value={sensor.device_id || ""}
                      onValueChange={(val) => {
                        const arr = [...additionalSensors];
                        arr[i].device_id = val;
                        setAdditionalSensors(arr);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Device ID" />
                      </SelectTrigger>
                      <SelectContent>
                        {additionalSensorDeviceOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      required
                      type="number"
                      min={1}
                      max={500}
                      placeholder="Slave ID"
                      value={sensor.slave_id}
                      onChange={(e) => {
                        const arr = [...additionalSensors];
                        arr[i].slave_id = e.target.value;
                        setAdditionalSensors(arr);
                      }}
                    />
                    <Input
                      required
                      type="number"
                      min={0}
                      placeholder="Start Address"
                      value={sensor.start_address}
                      onChange={(e) => {
                        const arr = [...additionalSensors];
                        arr[i].start_address = e.target.value;
                        setAdditionalSensors(arr);
                      }}
                    />
                    <Input
                      required
                      type="number"
                      min={1}
                      placeholder="Quantity"
                      value={sensor.quantity}
                      onChange={(e) => {
                        const arr = [...additionalSensors];
                        arr[i].quantity = e.target.value;
                        setAdditionalSensors(arr);
                      }}
                    />
                    <Select
                      value={sensor.data_type || ""}
                      onValueChange={(value) => {
                        const arr = [...additionalSensors];
                        arr[i].data_type = value;
                        setAdditionalSensors(arr);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Data Type" />
                      </SelectTrigger>
                      <SelectContent>
                        {MODBUS_DATA_TYPE_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select
                      value={sensor.order || ""}
                      onValueChange={(value) => {
                        const arr = [...additionalSensors];
                        arr[i].order = value;
                        setAdditionalSensors(arr);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Order" />
                      </SelectTrigger>
                      <SelectContent>
                        {MODBUS_ORDER_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      required
                      type="number"
                      step="0.01"
                      placeholder="Scale"
                      value={sensor.scale}
                      onChange={(e) => {
                        const arr = [...additionalSensors];
                        arr[i].scale = e.target.value;
                        setAdditionalSensors(arr);
                      }}
                    />
                    <Input
                      required
                      type="number"
                      step="0.01"
                      placeholder="Offset"
                      value={sensor.offset}
                      onChange={(e) => {
                        const arr = [...additionalSensors];
                        arr[i].offset = e.target.value;
                        setAdditionalSensors(arr);
                      }}
                    />
                  </div>
                ))}
                {additionalSensors.length > 0 && (
                  <Button
                    onClick={() =>
                      submitBulkAdditionalSensors(additionalSensors)
                    }
                  >
                    Submit Additional Sensors
                  </Button>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ═══ DEVICES ═══ */}
          <TabsContent value="devices" className="space-y-6">
            {/* Level Controllers */}
            <Card>
              <CardHeader>
                <CardTitle>Add Bulk Level Controllers</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    type="number"
                    max={20}
                    placeholder="Count"
                    value={levelCount}
                    onChange={(e) => setLevelCount(e.target.value)}
                  />
                  <Button
                    onClick={() =>
                      setLevelDevices(
                        generateRows(levelCount, {
                          device_id: "",
                          name: "",
                          network: {
                            connectionType: "",
                            gsmISP: "",
                            wifiUsn: "",
                            wifiPwd: "",
                          },
                        }),
                      )
                    }
                  >
                    Generate
                  </Button>
                </div>
                {levelDevices.map((d, i) => (
                  <div key={i} className="grid md:grid-cols-6 gap-3">
                    <Input
                      required
                      placeholder="Device ID"
                      value={d.device_id}
                      onChange={(e) => {
                        const arr = [...levelDevices];
                        arr[i].device_id = e.target.value;
                        setLevelDevices(arr);
                      }}
                    />
                    <Input
                      required
                      placeholder="Name"
                      value={d.name}
                      onChange={(e) => {
                        const arr = [...levelDevices];
                        arr[i].name = e.target.value;
                        setLevelDevices(arr);
                      }}
                    />
                    <Select
                      value={String(d.network?.connectionType || "")}
                      onValueChange={(val) => {
                        const arr = [...levelDevices];
                        arr[i].network = {
                          ...(arr[i].network || {}),
                          connectionType: Number(val),
                        };
                        setLevelDevices(arr);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Connection Type" />
                      </SelectTrigger>
                      <SelectContent>
                        {LEVEL_CONNECTION_TYPE_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select
                      value={
                        d.network?.gsmISP === null ||
                        d.network?.gsmISP === undefined ||
                        d.network?.gsmISP === ""
                          ? ""
                          : String(d.network.gsmISP)
                      }
                      onValueChange={(val) => {
                        const arr = [...levelDevices];
                        arr[i].network = {
                          ...(arr[i].network || {}),
                          gsmISP: Number(val),
                        };
                        setLevelDevices(arr);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="GSM ISP" />
                      </SelectTrigger>
                      <SelectContent>
                        {GSM_APN_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      placeholder="WiFi Username"
                      value={d.network?.wifiUsn || ""}
                      onChange={(e) => {
                        const arr = [...levelDevices];
                        arr[i].network = {
                          ...(arr[i].network || {}),
                          wifiUsn: e.target.value,
                        };
                        setLevelDevices(arr);
                      }}
                    />
                    <Input
                      placeholder="WiFi Password"
                      value={d.network?.wifiPwd || ""}
                      onChange={(e) => {
                        const arr = [...levelDevices];
                        arr[i].network = {
                          ...(arr[i].network || {}),
                          wifiPwd: e.target.value,
                        };
                        setLevelDevices(arr);
                      }}
                    />
                  </div>
                ))}
                {levelDevices.length > 0 && (
                  <Button
                    onClick={() => submitBulkLevelControllers(levelDevices)}
                  >
                    Submit
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Pump Controllers */}
            <Card>
              <CardHeader>
                <CardTitle>Add Bulk Pump Controllers</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    type="number"
                    placeholder="Count"
                    value={pumpControllerCount}
                    onChange={(e) => setPumpControllerCount(e.target.value)}
                  />
                  <Button
                    onClick={() =>
                      setPumpControllers(
                        generateRows(pumpControllerCount, {
                          device_id: "",
                          name: "",
                          network: {
                            connectionType: "",
                            gsmISP: "",
                            wifiUsn: "",
                            wifiPwd: "",
                          },
                        }),
                      )
                    }
                  >
                    Generate
                  </Button>
                </div>
                {pumpControllers.map((p, i) => (
                  <div key={i} className="grid md:grid-cols-6 gap-3">
                    <Input
                      required
                      placeholder="Device ID"
                      value={p.device_id}
                      onChange={(e) => {
                        const arr = [...pumpControllers];
                        arr[i].device_id = e.target.value;
                        setPumpControllers(arr);
                      }}
                    />
                    <Input
                      required
                      placeholder="Name"
                      value={p.name}
                      onChange={(e) => {
                        const arr = [...pumpControllers];
                        arr[i].name = e.target.value;
                        setPumpControllers(arr);
                      }}
                    />
                    <Select
                      value={String(p.network?.connectionType || "")}
                      onValueChange={(val) => {
                        const arr = [...pumpControllers];
                        arr[i].network = {
                          ...(arr[i].network || {}),
                          connectionType: Number(val),
                        };
                        setPumpControllers(arr);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Connection Type" />
                      </SelectTrigger>
                      <SelectContent>
                        {LEVEL_CONNECTION_TYPE_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select
                      value={
                        p.network?.gsmISP === null ||
                        p.network?.gsmISP === undefined ||
                        p.network?.gsmISP === ""
                          ? ""
                          : String(p.network.gsmISP)
                      }
                      onValueChange={(val) => {
                        const arr = [...pumpControllers];
                        arr[i].network = {
                          ...(arr[i].network || {}),
                          gsmISP: Number(val),
                        };
                        setPumpControllers(arr);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="GSM ISP" />
                      </SelectTrigger>
                      <SelectContent>
                        {GSM_APN_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      placeholder="WiFi Username"
                      value={p.network?.wifiUsn || ""}
                      onChange={(e) => {
                        const arr = [...pumpControllers];
                        arr[i].network = {
                          ...(arr[i].network || {}),
                          wifiUsn: e.target.value,
                        };
                        setPumpControllers(arr);
                      }}
                    />
                    <Input
                      placeholder="WiFi Password"
                      value={p.network?.wifiPwd || ""}
                      onChange={(e) => {
                        const arr = [...pumpControllers];
                        arr[i].network = {
                          ...(arr[i].network || {}),
                          wifiPwd: e.target.value,
                        };
                        setPumpControllers(arr);
                      }}
                    />
                  </div>
                ))}
                {pumpControllers.length > 0 && (
                  <Button
                    onClick={() => submitBulkPumpControllers(pumpControllers)}
                  >
                    Submit
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Borewell Pump Controllers */}
            <Card>
              <CardHeader>
                <CardTitle>Add Bulk Borewell Pump Controllers</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    type="number"
                    placeholder="Count"
                    value={borewellPumpControllerCount}
                    onChange={(e) =>
                      setBorewellPumpControllerCount(e.target.value)
                    }
                  />
                  <Button
                    onClick={() =>
                      setBorewellPumpControllers(
                        generateRows(borewellPumpControllerCount, {
                          device_id: "",
                          name: "",
                          network: {
                            connectionType: "",
                            gsmISP: "",
                            wifiUsn: "",
                            wifiPwd: "",
                          },
                        }),
                      )
                    }
                  >
                    Generate
                  </Button>
                </div>
                {borewellPumpControllers.map((controller, i) => (
                  <div
                    key={i}
                    className="grid md:grid-cols-6 xl:grid-cols-7 gap-3"
                  >
                    <Input
                      required
                      placeholder="Device ID"
                      value={controller.device_id}
                      onChange={(e) => {
                        const arr = [...borewellPumpControllers];
                        arr[i].device_id = e.target.value;
                        setBorewellPumpControllers(arr);
                      }}
                    />
                    <Input
                      required
                      placeholder="Name"
                      value={controller.name}
                      onChange={(e) => {
                        const arr = [...borewellPumpControllers];
                        arr[i].name = e.target.value;
                        setBorewellPumpControllers(arr);
                      }}
                    />
                    <Select
                      value={String(controller.network?.connectionType || "")}
                      onValueChange={(val) => {
                        const arr = [...borewellPumpControllers];
                        arr[i].network = {
                          ...(arr[i].network || {}),
                          connectionType: Number(val),
                        };
                        setBorewellPumpControllers(arr);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Connection Type" />
                      </SelectTrigger>
                      <SelectContent>
                        {LEVEL_CONNECTION_TYPE_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select
                      value={
                        controller.network?.gsmISP === null ||
                        controller.network?.gsmISP === undefined ||
                        controller.network?.gsmISP === ""
                          ? ""
                          : String(controller.network.gsmISP)
                      }
                      onValueChange={(val) => {
                        const arr = [...borewellPumpControllers];
                        arr[i].network = {
                          ...(arr[i].network || {}),
                          gsmISP: Number(val),
                        };
                        setBorewellPumpControllers(arr);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="GSM ISP" />
                      </SelectTrigger>
                      <SelectContent>
                        {GSM_APN_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      placeholder="WiFi Username"
                      value={controller.network?.wifiUsn || ""}
                      onChange={(e) => {
                        const arr = [...borewellPumpControllers];
                        arr[i].network = {
                          ...(arr[i].network || {}),
                          wifiUsn: e.target.value,
                        };
                        setBorewellPumpControllers(arr);
                      }}
                    />
                    <Input
                      placeholder="WiFi Password"
                      value={controller.network?.wifiPwd || ""}
                      onChange={(e) => {
                        const arr = [...borewellPumpControllers];
                        arr[i].network = {
                          ...(arr[i].network || {}),
                          wifiPwd: e.target.value,
                        };
                        setBorewellPumpControllers(arr);
                      }}
                    />
                  </div>
                ))}
                {borewellPumpControllers.length > 0 && (
                  <Button
                    onClick={() =>
                      submitBulkBorewellPumpControllers(borewellPumpControllers)
                    }
                  >
                    Submit
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Pumps */}
            <Card>
              <CardHeader>
                <CardTitle>Add Bulk Pumps</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    type="number"
                    placeholder="Count"
                    value={pumpCount}
                    onChange={(e) => setPumpCount(e.target.value)}
                  />
                  <Button
                    onClick={() =>
                      setPumps(
                        generateRows(pumpCount, {
                          pump_id: "",
                          name: "",
                          power_rating: "",
                          model: "",
                          energymeter: {
                            status: "",
                            make: "",
                            slave_id: "",
                          },
                          control_type: "",
                          vfd: {
                            slave_id: "",
                            pressure_slave_id: "",
                            pressure_start_address: "",
                            pressure_quantity: "",
                            pressure_data_type: "",
                            pressure_order: "",
                            pressure_scale: "",
                            pressure_offset: "",
                            range_low_bar: "",
                            range_high_bar: "",
                            min_freq_hz: "",
                            max_freq_hz: "",
                          },
                          pressure_range: {
                            is_source: true,
                            pt_id: "",
                            stage_index: "",
                            pt_slave_id: "",
                            pt_start_address: "",
                            pt_quantity: "",
                            pt_data_type: "",
                            pt_order: "",
                            pt_scale: "",
                            pt_offset: "",
                            range_low_bar: "",
                            range_high_bar: "",
                          },
                          control: false,
                        }),
                      )
                    }
                  >
                    Generate
                  </Button>
                </div>
                {pumps.map((pump, i) => (
                  <div
                    key={i}
                    className="grid md:grid-cols-6 xl:grid-cols-8 gap-3"
                  >
                    <Input
                      required
                      placeholder="Pump ID"
                      value={pump.pump_id}
                      onChange={(e) => {
                        const arr = [...pumps];
                        arr[i].pump_id = e.target.value;
                        setPumps(arr);
                      }}
                    />
                    <Input
                      required
                      placeholder="Name"
                      value={pump.name}
                      onChange={(e) => {
                        const arr = [...pumps];
                        arr[i].name = e.target.value;
                        setPumps(arr);
                      }}
                    />
                    <Input
                      required
                      type="number"
                      placeholder="Power Rating"
                      value={pump.power_rating}
                      onChange={(e) => {
                        const arr = [...pumps];
                        arr[i].power_rating = e.target.value;
                        setPumps(arr);
                      }}
                    />
                    <Input
                      placeholder="Model"
                      value={pump.model}
                      onChange={(e) => {
                        const arr = [...pumps];
                        arr[i].model = e.target.value;
                        setPumps(arr);
                      }}
                    />
                    <Select
                      value={String(pump.energymeter?.status ?? "")}
                      onValueChange={(value) => {
                        const arr = [...pumps];
                        arr[i].energymeter = {
                          ...(arr[i].energymeter || {}),
                          status: value,
                        };
                        setPumps(arr);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="EM Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">Connected</SelectItem>
                        <SelectItem value="0">Not connected</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select
                      value={String(pump.energymeter?.make ?? "")}
                      onValueChange={(value) => {
                        const arr = [...pumps];
                        arr[i].energymeter = {
                          ...(arr[i].energymeter || {}),
                          make: value,
                        };
                        setPumps(arr);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="EM Make" />
                      </SelectTrigger>
                      <SelectContent>
                        {ENERGY_METER_MAKE_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      required
                      type="number"
                      min={1}
                      max={500}
                      placeholder="EM Slave ID"
                      value={pump.energymeter?.slave_id ?? ""}
                      onChange={(e) => {
                        const arr = [...pumps];
                        arr[i].energymeter = {
                          ...(arr[i].energymeter || {}),
                          slave_id: e.target.value,
                        };
                        setPumps(arr);
                      }}
                    />
                    <Select
                      value={String(pump.control_type ?? "")}
                      onValueChange={(value) => {
                        const arr = [...pumps];
                        arr[i].control_type = value;
                        setPumps(arr);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Control Type" />
                      </SelectTrigger>
                      <SelectContent>
                        {PUMP_CONTROL_TYPE_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {isVfdPumpControlType(pump.control_type) && (
                      <>
                        <Input
                          required
                          type="number"
                          min={0}
                          max={500}
                          placeholder="VFD Slave ID"
                          value={pump.vfd?.slave_id ?? ""}
                          onChange={(e) => {
                            const arr = [...pumps];
                            arr[i].vfd = {
                              ...(arr[i].vfd || {}),
                              slave_id: e.target.value,
                            };
                            setPumps(arr);
                          }}
                        />
                        <Input
                          required
                          type="number"
                          min={0}
                          max={500}
                          placeholder="Pressure Slave ID"
                          value={pump.vfd?.pressure_slave_id ?? ""}
                          onChange={(e) => {
                            const arr = [...pumps];
                            arr[i].vfd = {
                              ...(arr[i].vfd || {}),
                              pressure_slave_id: e.target.value,
                            };
                            setPumps(arr);
                          }}
                        />
                        <Input
                          required
                          type="number"
                          min={0}
                          placeholder="Pressure Start Address"
                          value={pump.vfd?.pressure_start_address ?? ""}
                          onChange={(e) => {
                            const arr = [...pumps];
                            arr[i].vfd = {
                              ...(arr[i].vfd || {}),
                              pressure_start_address: e.target.value,
                            };
                            setPumps(arr);
                          }}
                        />
                        <Input
                          required
                          type="number"
                          min={0}
                          placeholder="Pressure Quantity"
                          value={pump.vfd?.pressure_quantity ?? ""}
                          onChange={(e) => {
                            const arr = [...pumps];
                            arr[i].vfd = {
                              ...(arr[i].vfd || {}),
                              pressure_quantity: e.target.value,
                            };
                            setPumps(arr);
                          }}
                        />
                        <Select
                          value={String(pump.vfd?.pressure_data_type ?? "")}
                          onValueChange={(value) => {
                            const arr = [...pumps];
                            arr[i].vfd = {
                              ...(arr[i].vfd || {}),
                              pressure_data_type: value,
                            };
                            setPumps(arr);
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Pressure Data Type" />
                          </SelectTrigger>
                          <SelectContent>
                            {MODBUS_DATA_TYPE_OPTIONS.map((option) => (
                              <SelectItem
                                key={option.value}
                                value={option.value}
                              >
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Select
                          value={String(pump.vfd?.pressure_order ?? "")}
                          onValueChange={(value) => {
                            const arr = [...pumps];
                            arr[i].vfd = {
                              ...(arr[i].vfd || {}),
                              pressure_order: value,
                            };
                            setPumps(arr);
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Pressure Order" />
                          </SelectTrigger>
                          <SelectContent>
                            {MODBUS_ORDER_OPTIONS.map((option) => (
                              <SelectItem
                                key={option.value}
                                value={option.value}
                              >
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Input
                          required
                          type="number"
                          step="0.01"
                          placeholder="Pressure Scale"
                          value={pump.vfd?.pressure_scale ?? ""}
                          onChange={(e) => {
                            const arr = [...pumps];
                            arr[i].vfd = {
                              ...(arr[i].vfd || {}),
                              pressure_scale: e.target.value,
                            };
                            setPumps(arr);
                          }}
                        />
                        <Input
                          required
                          type="number"
                          step="0.01"
                          placeholder="Pressure Offset"
                          value={pump.vfd?.pressure_offset ?? ""}
                          onChange={(e) => {
                            const arr = [...pumps];
                            arr[i].vfd = {
                              ...(arr[i].vfd || {}),
                              pressure_offset: e.target.value,
                            };
                            setPumps(arr);
                          }}
                        />
                        <Input
                          required
                          type="number"
                          step="0.01"
                          placeholder="Range Low Bar"
                          value={pump.vfd?.range_low_bar ?? ""}
                          onChange={(e) => {
                            const arr = [...pumps];
                            arr[i].vfd = {
                              ...(arr[i].vfd || {}),
                              range_low_bar: e.target.value,
                            };
                            setPumps(arr);
                          }}
                        />
                        <Input
                          required
                          type="number"
                          step="0.01"
                          placeholder="Range High Bar"
                          value={pump.vfd?.range_high_bar ?? ""}
                          onChange={(e) => {
                            const arr = [...pumps];
                            arr[i].vfd = {
                              ...(arr[i].vfd || {}),
                              range_high_bar: e.target.value,
                            };
                            setPumps(arr);
                          }}
                        />
                        <Input
                          required
                          type="number"
                          step="0.01"
                          placeholder="Min Freq Hz"
                          value={pump.vfd?.min_freq_hz ?? ""}
                          onChange={(e) => {
                            const arr = [...pumps];
                            arr[i].vfd = {
                              ...(arr[i].vfd || {}),
                              min_freq_hz: e.target.value,
                            };
                            setPumps(arr);
                          }}
                        />
                        <Input
                          required
                          type="number"
                          step="0.01"
                          placeholder="Max Freq Hz"
                          value={pump.vfd?.max_freq_hz ?? ""}
                          onChange={(e) => {
                            const arr = [...pumps];
                            arr[i].vfd = {
                              ...(arr[i].vfd || {}),
                              max_freq_hz: e.target.value,
                            };
                            setPumps(arr);
                          }}
                        />
                      </>
                    )}
                    {Number(pump.control_type) === 2 && (
                      <>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={Boolean(pump.pressure_range?.is_source)}
                            onCheckedChange={(val) => {
                              const arr = [...pumps];
                              arr[i].pressure_range = {
                                ...(arr[i].pressure_range || {}),
                                is_source: val,
                              };
                              setPumps(arr);
                            }}
                          />
                          <span className="text-sm">PR Source</span>
                        </div>
                        <Input
                          required
                          placeholder="Pressure Range PT ID"
                          value={pump.pressure_range?.pt_id ?? ""}
                          onChange={(e) => {
                            const arr = [...pumps];
                            arr[i].pressure_range = {
                              ...(arr[i].pressure_range || {}),
                              pt_id: e.target.value,
                            };
                            setPumps(arr);
                          }}
                        />
                        <Input
                          required
                          type="number"
                          min={0}
                          placeholder="Pressure Stage Index"
                          value={pump.pressure_range?.stage_index ?? ""}
                          onChange={(e) => {
                            const arr = [...pumps];
                            arr[i].pressure_range = {
                              ...(arr[i].pressure_range || {}),
                              stage_index: e.target.value,
                            };
                            setPumps(arr);
                          }}
                        />
                        <Input
                          required
                          type="number"
                          min={0}
                          max={500}
                          placeholder="Pressure PT Slave ID"
                          value={pump.pressure_range?.pt_slave_id ?? ""}
                          onChange={(e) => {
                            const arr = [...pumps];
                            arr[i].pressure_range = {
                              ...(arr[i].pressure_range || {}),
                              pt_slave_id: e.target.value,
                            };
                            setPumps(arr);
                          }}
                        />
                        <Input
                          required
                          type="number"
                          min={0}
                          placeholder="Pressure PT Start Address"
                          value={pump.pressure_range?.pt_start_address ?? ""}
                          onChange={(e) => {
                            const arr = [...pumps];
                            arr[i].pressure_range = {
                              ...(arr[i].pressure_range || {}),
                              pt_start_address: e.target.value,
                            };
                            setPumps(arr);
                          }}
                        />
                        <Input
                          required
                          type="number"
                          min={0}
                          placeholder="Pressure PT Quantity"
                          value={pump.pressure_range?.pt_quantity ?? ""}
                          onChange={(e) => {
                            const arr = [...pumps];
                            arr[i].pressure_range = {
                              ...(arr[i].pressure_range || {}),
                              pt_quantity: e.target.value,
                            };
                            setPumps(arr);
                          }}
                        />
                        <Input
                          required
                          type="number"
                          min={0}
                          placeholder="Pressure PT Data Type"
                          value={pump.pressure_range?.pt_data_type ?? ""}
                          onChange={(e) => {
                            const arr = [...pumps];
                            arr[i].pressure_range = {
                              ...(arr[i].pressure_range || {}),
                              pt_data_type: e.target.value,
                            };
                            setPumps(arr);
                          }}
                        />
                        <Input
                          required
                          type="number"
                          min={0}
                          placeholder="Pressure PT Order"
                          value={pump.pressure_range?.pt_order ?? ""}
                          onChange={(e) => {
                            const arr = [...pumps];
                            arr[i].pressure_range = {
                              ...(arr[i].pressure_range || {}),
                              pt_order: e.target.value,
                            };
                            setPumps(arr);
                          }}
                        />
                        <Input
                          required
                          type="number"
                          step="0.01"
                          placeholder="Pressure PT Scale"
                          value={pump.pressure_range?.pt_scale ?? ""}
                          onChange={(e) => {
                            const arr = [...pumps];
                            arr[i].pressure_range = {
                              ...(arr[i].pressure_range || {}),
                              pt_scale: e.target.value,
                            };
                            setPumps(arr);
                          }}
                        />
                        <Input
                          required
                          type="number"
                          step="0.01"
                          placeholder="Pressure PT Offset"
                          value={pump.pressure_range?.pt_offset ?? ""}
                          onChange={(e) => {
                            const arr = [...pumps];
                            arr[i].pressure_range = {
                              ...(arr[i].pressure_range || {}),
                              pt_offset: e.target.value,
                            };
                            setPumps(arr);
                          }}
                        />
                        <Input
                          required
                          type="number"
                          step="0.01"
                          placeholder="Pressure Range Low Bar"
                          value={pump.pressure_range?.range_low_bar ?? ""}
                          onChange={(e) => {
                            const arr = [...pumps];
                            arr[i].pressure_range = {
                              ...(arr[i].pressure_range || {}),
                              range_low_bar: e.target.value,
                            };
                            setPumps(arr);
                          }}
                        />
                        <Input
                          required
                          type="number"
                          step="0.01"
                          placeholder="Pressure Range High Bar"
                          value={pump.pressure_range?.range_high_bar ?? ""}
                          onChange={(e) => {
                            const arr = [...pumps];
                            arr[i].pressure_range = {
                              ...(arr[i].pressure_range || {}),
                              range_high_bar: e.target.value,
                            };
                            setPumps(arr);
                          }}
                        />
                      </>
                    )}
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={pump.control}
                        onCheckedChange={(val) => {
                          const arr = [...pumps];
                          arr[i].control = val;
                          setPumps(arr);
                        }}
                      />
                      <span className="text-sm">Control</span>
                    </div>
                  </div>
                ))}
                {pumps.length > 0 && (
                  <Button onClick={() => submitBulkPumps(pumps)}>
                    Submit Pumps
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Valves */}
            <Card>
              <CardHeader>
                <CardTitle>Add Bulk Valves</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    type="number"
                    placeholder="Count"
                    value={valveCount}
                    onChange={(e) => setValveCount(e.target.value)}
                  />
                  <Button
                    onClick={() =>
                      setValves(
                        generateRows(valveCount, {
                          valve_id: "",
                          type: "motorized",
                          control: false,
                        }),
                      )
                    }
                  >
                    Generate
                  </Button>
                </div>
                {valves.map((v, i) => (
                  <div key={i} className="grid md:grid-cols-3 gap-3">
                    <Input
                      required
                      placeholder="Valve ID"
                      value={v.valve_id}
                      onChange={(e) => {
                        const arr = [...valves];
                        arr[i].valve_id = e.target.value;
                        setValves(arr);
                      }}
                    />
                    <Select
                      value={v.type}
                      onValueChange={(val) => {
                        const arr = [...valves];
                        arr[i].type = val;
                        setValves(arr);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="motorized">Motorized</SelectItem>
                        <SelectItem value="manual">Manual</SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={v.control}
                        onCheckedChange={(val) => {
                          const arr = [...valves];
                          arr[i].control = val;
                          setValves(arr);
                        }}
                      />
                      <span className="text-sm">Control</span>
                    </div>
                  </div>
                ))}
                {valves.length > 0 && (
                  <Button onClick={() => submitBulkValves(valves)}>
                    Submit Valves
                  </Button>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ═══ ARCHITECTURE ═══ */}
          <TabsContent value="architecture">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Architecture Configuration</CardTitle>
                    <CardDescription className="mt-1">
                      {savedFlows.length === 0
                        ? 'No flows configured yet. Click "Add Flow" to map your first water flow path.'
                        : `${savedFlows.length} flow${savedFlows.length > 1 ? "s" : ""} configured`}
                    </CardDescription>
                  </div>
                  <Button
                    onClick={openNewFlow}
                    className="flex items-center gap-2 bg-slate-900 hover:bg-slate-700 text-white"
                  >
                    <Plus className="h-4 w-4" />
                    Add Flow
                  </Button>
                </div>
              </CardHeader>

              <CardContent>
                {savedFlows.length === 0 ? (
                  /* Empty state */
                  <div className="flex flex-col items-center justify-center py-20 text-center">
                    <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                      <GitBranch className="h-8 w-8 text-slate-400" />
                    </div>
                    <p className="text-slate-500 text-sm max-w-xs">
                      Map your society's complete water distribution path — from
                      underground tanks all the way to individual overhead
                      tanks.
                    </p>
                    <Button
                      onClick={openNewFlow}
                      variant="outline"
                      className="mt-6 flex items-center gap-2"
                    >
                      <Plus className="h-4 w-4" /> Add Your First Flow
                    </Button>
                  </div>
                ) : (
                  /* Saved flows */
                  <div className="space-y-4">
                    {savedFlows.map((flow, index) => (
                      <div
                        key={flow.id}
                        className="rounded-xl border border-slate-200 bg-slate-50/50 overflow-hidden"
                      >
                        {/* Flow Container */}
                        <div className="p-5 relative">
                          {/* Flow index badge */}
                          <div className="absolute top-4 left-4 w-6 h-6 rounded-full bg-slate-800 text-white text-xs font-bold flex items-center justify-center">
                            {index + 1}
                          </div>

                          <div className="pl-8">
                            <FlowTree flow={flow} {...treeProps} />
                          </div>
                        </div>

                        {/* Actions Section */}
                        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-100/50">
                          <button
                            onClick={() => openEditFlow(flow)}
                            className="p-2 rounded-lg hover:bg-blue-100 text-blue-600 hover:text-blue-700 transition-all"
                            title="Edit flow"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => deleteFlow(flow.id)}
                            className="p-2 rounded-lg hover:bg-red-100 text-red-600 hover:text-red-700 transition-all"
                            title="Delete flow"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default DeviceRegistration;
